import * as Sentry from '@sentry/react';
import { createFileRoute } from '@tanstack/react-router';
import {
    ChevronDown,
    Loader2,
    Settings,
    SlidersHorizontal,
    Sparkles,
    X,
} from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { ExtensionNavigationGuard } from '@apps/web/components/extension_navigation_guard';
import { OutreachFeedCompact } from '@apps/web/components/home/outreach_feed_compact';
import { OrganizationMembershipPickerContent } from '@apps/web/components/layout/organization_membership_picker';
import { NotificationBell } from '@apps/web/components/notification_bell';
import { NotificationTabTitle } from '@apps/web/components/notification_tab_title';
import {
    PhoebeChatView,
    StructuredReportArtifactsPanel,
} from '@apps/web/components/phoebe_chat_view';
import {
    OrganizationConversationDetailPane,
    OrganizationConversationListPanel,
} from '@apps/web/components/shared/messages/organization_conversation_panels';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Separator } from '@apps/web/components/ui/separator';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import { useOrganizationContext } from '@apps/web/context/organization_provider';
import { useSessionState } from '@apps/web/context/session_state_provider';
import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import {
    useOrganizationConversationsRealtime,
    useOrganizationPendingMessagesCountRealtime,
    useOrganizationUnreadCountRealtime,
} from '@apps/web/hooks/realtime_hooks';
import { useCoalescedCallback } from '@apps/web/hooks/use_coalesced_callback';
import { useListenForOrganizationEvent } from '@apps/web/hooks/use_listen_for_organization_event';
import { useOrganizationEvents } from '@apps/web/hooks/use_organization_events';
import { useThrottledCallback } from '@apps/web/hooks/use_throttled_callback';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { isGeneralAgentLifecycleEligible } from '@apps/web/lib/chat_access';
import { logExtensionEvent } from '@apps/web/lib/datadog_logs';
import { formatDate, formatTime } from '@apps/web/lib/date_utils';
import { EVENTS } from '@apps/web/lib/events';
import { findFixtureOrganizationId } from '@apps/web/lib/extension_fixture_organization';
import {
    CHROME_EXTENSION_INSTALL_URL,
    type ChromeExtensionInstallType,
    isChromeExtensionInstallType,
    PHOEBE_CHROME_EXTENSION_ID,
} from '@apps/web/lib/extension_install_prompt';
import { handleExtensionAppLinkClickCapture } from '@apps/web/lib/extension_link_handoff';
import {
    DEFAULT_EXTENSION_OUTREACH_FEED_SORT,
    EXTENSION_OUTREACH_FEED_SORT_OPTIONS,
    type ExtensionOutreachFeedSort,
    isExtensionOutreachFeedSort,
    sortExtensionOutreachFeedItems,
} from '@apps/web/lib/extension_outreach_feed_sorting';
import { ModeOverrideProvider } from '@apps/web/lib/mode_store';
import {
    buildOrganizationMembershipOptions,
    formatOrganizationMembershipDisplayName,
} from '@apps/web/lib/organization_display';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { isCancelledShift } from '@apps/web/lib/shift_cancellation';
import { cn } from '@apps/web/lib/utils';
import {
    buildExtensionOrganizationStorageKey,
    readPersistedExtensionOrganizationId,
    resolveExtensionOrganizationId,
    writePersistedExtensionOrganizationId,
} from '@apps/web/routes/_app/(extension)/extension/organization_resolution';
import { ExtensionShiftRangeSection } from '@apps/web/routes/_app/(extension)/extension/shift_range_section';
import {
    type ExtensionResolveContextRequest,
    type HomeFeedItem,
    useConversationActions,
    useExtensionConfig,
    useExtensionResolveContext,
    useHomeFeed,
    useJoinedOutreach,
    useOrganization,
    useOutreachesBatch,
    useShift,
    useShiftSearch,
    useUser,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ApiResponseType,
    type components,
    type OutreachStatus,
    type Shift,
    type User,
} from '@libraries/typescript/api_client';
import {
    EXTENSION_BOTTOM_BAR_ENABLED,
    EXTENSION_MINIMIZED_RAIL_DEFAULT_ENABLED,
    EXTENSION_MINIMIZED_RAIL_ENABLED,
    EXTENSION_SIDEBAR_OPEN_STATE_SYNC_DEFAULT_ENABLED,
} from '@libraries/typescript/utilities/extension_surface_flags';
import { buildShiftIdentityKey } from '@libraries/typescript/utilities/shift_identity';

export const Route = createFileRoute('/_app/(extension)/extension')({
    component: ExtensionPage,
    head: () => ({
        meta: [{ title: 'Extension | Phoebe' }],
    }),
});

type Provider = 'wellsky' | 'axiscare' | 'hhaexchange' | 'generations_homecare';
type EntityType = 'shifts' | 'caregivers' | 'clients' | 'care_coordinators';
type ResolvableEntityType = 'shifts' | 'caregivers' | 'clients';
type ExtensionSidebarTab = 'outreaches' | 'conversations' | 'page_shifts';

type PageInfo = {
    readonly provider: Provider | null;
    readonly url: string;
    readonly entityType?: EntityType;
    readonly externalId?: string;
};

type ResolvablePageInfo = PageInfo & {
    readonly provider: Provider;
    readonly entityType: ResolvableEntityType;
    readonly externalId: string;
};

type FeedShiftActionUpdate = {
    readonly provider: Provider;
    readonly label: string;
    readonly variant: 'fill' | 'assign' | 'open';
    readonly externalShiftId?: string;
    readonly matchKey?: string;
    readonly outreachId?: string;
    readonly sourceUrl: string;
};

function areFeedShiftActionUpdatesEqual(
    left: FeedShiftActionUpdate | null,
    right: FeedShiftActionUpdate | null,
): boolean {
    if (left === right) {
        return true;
    }
    if (left === null || right === null) {
        return false;
    }

    return (
        left.provider === right.provider &&
        left.externalShiftId === right.externalShiftId &&
        left.label === right.label &&
        left.matchKey === right.matchKey &&
        left.outreachId === right.outreachId &&
        left.sourceUrl === right.sourceUrl &&
        left.variant === right.variant
    );
}

type SelectedExtensionShift = {
    readonly provider: Provider;
    readonly externalShiftId: string;
    readonly sourceUrl: string;
    // WellSky-only: per-date composite key from the calendar event class
    // name (`{externalShiftId}_{YYYY-MM-DD}`). Forwarded to the backend
    // batch-resolve endpoint so recurring-shift templates disambiguate
    // to the exact per-date Phoebe shift. AxisCare/HHAExchange/non-
    // calendar surfaces leave it undefined.
    readonly wellskyShiftUniqueId?: string;
};

type ExtensionPageShift = {
    readonly provider: Provider;
    readonly externalShiftId: string;
    readonly sourceUrl: string;
    readonly label: string;
    readonly variant: 'fill' | 'assign' | 'open';
    readonly activeOutreachId: string | null;
    readonly matchKey?: string;
    readonly wellskyShiftUniqueId?: string;
    readonly title: string;
    readonly subtitle?: string;
    readonly details?: string;
};

function isProvider(value: unknown): value is Provider {
    return (
        value === 'axiscare' ||
        value === 'wellsky' ||
        value === 'hhaexchange' ||
        value === 'generations_homecare'
    );
}

function buildShiftPostMessageIdentity(shift: {
    readonly externalShiftId: string;
    readonly wellskyShiftUniqueId?: string | null;
}): {
    readonly externalShiftId: string;
    readonly wellskyShiftUniqueId?: string;
} {
    return {
        externalShiftId: shift.externalShiftId,
        ...(shift.wellskyShiftUniqueId
            ? { wellskyShiftUniqueId: shift.wellskyShiftUniqueId }
            : {}),
    };
}

function isSelectableRecurringWellSkyPageShift(
    shift: ExtensionPageShift,
    selectedShift: SelectedExtensionShift,
): shift is ExtensionPageShift & { readonly wellskyShiftUniqueId: string } {
    return (
        selectedShift.provider === 'wellsky' &&
        selectedShift.wellskyShiftUniqueId !== undefined &&
        shift.provider === selectedShift.provider &&
        shift.externalShiftId === selectedShift.externalShiftId &&
        shift.sourceUrl === selectedShift.sourceUrl &&
        shift.wellskyShiftUniqueId !== undefined &&
        shift.variant === 'fill' &&
        shift.activeOutreachId === null
    );
}

function hasSelectedRecurringWellSkyGroupMember(
    shifts: ReadonlyArray<SelectedExtensionShift>,
    selectedShift: SelectedExtensionShift,
): boolean {
    return shifts.some(
        (shift) =>
            shift.provider === 'wellsky' &&
            selectedShift.provider === 'wellsky' &&
            shift.externalShiftId === selectedShift.externalShiftId &&
            shift.sourceUrl === selectedShift.sourceUrl &&
            shift.wellskyShiftUniqueId !== undefined &&
            selectedShift.wellskyShiftUniqueId !== undefined,
    );
}

function buildInitialSelectedEhrShiftGroup(options: {
    readonly shift: SelectedExtensionShift;
    readonly currentSelectedShifts: ReadonlyArray<SelectedExtensionShift>;
    readonly pageShifts: ReadonlyArray<ExtensionPageShift>;
}): ReadonlyArray<SelectedExtensionShift> {
    if (
        hasSelectedRecurringWellSkyGroupMember(
            options.currentSelectedShifts,
            options.shift,
        )
    ) {
        return [options.shift];
    }

    const recurringGroup = options.pageShifts
        .filter((pageShift) =>
            isSelectableRecurringWellSkyPageShift(pageShift, options.shift),
        )
        .map((pageShift) => ({
            provider: pageShift.provider,
            externalShiftId: pageShift.externalShiftId,
            sourceUrl: pageShift.sourceUrl,
            wellskyShiftUniqueId: pageShift.wellskyShiftUniqueId,
        }));

    return recurringGroup.length > 0 ? recurringGroup : [options.shift];
}

function areSelectedExtensionShiftsEqual(
    left: ReadonlyArray<SelectedExtensionShift>,
    right: ReadonlyArray<SelectedExtensionShift>,
): boolean {
    return (
        left.length === right.length &&
        left.every((shift, index) => {
            const otherShift = right[index];
            return (
                otherShift !== undefined &&
                shift.provider === otherShift.provider &&
                shift.externalShiftId === otherShift.externalShiftId &&
                shift.sourceUrl === otherShift.sourceUrl &&
                shift.wellskyShiftUniqueId === otherShift.wellskyShiftUniqueId
            );
        })
    );
}

function areExtensionPageShiftsEqual(
    left: ReadonlyArray<ExtensionPageShift>,
    right: ReadonlyArray<ExtensionPageShift>,
): boolean {
    return (
        left.length === right.length &&
        left.every((shift, index) => {
            const otherShift = right[index];
            return (
                otherShift !== undefined &&
                shift.provider === otherShift.provider &&
                shift.externalShiftId === otherShift.externalShiftId &&
                shift.sourceUrl === otherShift.sourceUrl &&
                shift.label === otherShift.label &&
                shift.variant === otherShift.variant &&
                shift.activeOutreachId === otherShift.activeOutreachId &&
                shift.matchKey === otherShift.matchKey &&
                shift.title === otherShift.title &&
                shift.subtitle === otherShift.subtitle &&
                shift.details === otherShift.details &&
                shift.wellskyShiftUniqueId === otherShift.wellskyShiftUniqueId
            );
        })
    );
}

type OtherOrgShiftMatch = {
    readonly organizationId: string;
    readonly organizationName: string | null;
    readonly phoebeShiftId: string;
    readonly patientName: string | null;
    readonly shiftStartTime: string | null;
    readonly shiftEndTime: string | null;
};

type BatchResolvedItem = {
    readonly externalShiftId: string;
    readonly wellskyShiftUniqueId: string | null;
    readonly status: 'resolved' | 'not_found' | 'other_org' | 'missing';
    readonly phoebeShiftId: string | null;
    readonly patientName: string | null;
    readonly shiftStartTime: string | null;
    readonly shiftEndTime: string | null;
    readonly shiftStatus: Shift['status'] | null;
    // Populated when `status === 'other_org'`: every Phoebe org the user
    // belongs to that has a snapshot of this EHR shift. The same shift
    // can appear in multiple orgs (franchise locations sharing one EHR
    // account, or a real org + sandbox org pair), so this is a list.
    // Ordered by sync recency.
    readonly otherOrgMatches: ReadonlyArray<OtherOrgShiftMatch>;
};

// Alias the generated OpenAPI schema rather than restating its shape. A
// hand-written copy silently drops fields the API starts returning — that
// is how `shift_status` went missing and let cancelled shifts through to
// the outreach composer.
type BatchResolveShiftResponseItem =
    components['schemas']['BatchResolveShiftItem'];

type BatchResolveShiftsResponse = {
    readonly items: ReadonlyArray<BatchResolveShiftResponseItem>;
};

// Keep the sidebar request fan-out aligned with the server-side request cap so
// large multi-select batches degrade into multiple bounded requests instead of
// failing the entire selection with a 422.
const MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS = 100;
const PAGE_SHIFT_BATCH_RESOLVE_CACHE_TTL_MS = 30_000;
const PAGE_SHIFT_BATCH_RESOLVE_CACHE_MAX_ENTRIES = 256;

const pageShiftBatchResolveCache = new Map<
    string,
    {
        readonly expiresAtMs: number;
        readonly items: ReadonlyArray<BatchResolveShiftResponseItem>;
    }
>();
const pageShiftBatchResolveInflightRequests = new Map<
    string,
    Promise<ReadonlyArray<BatchResolveShiftResponseItem>>
>();

function chunkReadonlyArray<T>(
    values: ReadonlyArray<T>,
    chunkSize: number,
): Array<ReadonlyArray<T>> {
    const chunks: Array<ReadonlyArray<T>> = [];

    for (let index = 0; index < values.length; index += chunkSize) {
        chunks.push(values.slice(index, index + chunkSize));
    }

    return chunks;
}

function prunePageShiftBatchResolveCache(nowMs = Date.now()): void {
    for (const [key, entry] of pageShiftBatchResolveCache) {
        if (entry.expiresAtMs <= nowMs) {
            pageShiftBatchResolveCache.delete(key);
        }
    }

    while (
        pageShiftBatchResolveCache.size >
        PAGE_SHIFT_BATCH_RESOLVE_CACHE_MAX_ENTRIES
    ) {
        const oldestKey = pageShiftBatchResolveCache.keys().next().value;
        if (oldestKey === undefined) {
            return;
        }
        pageShiftBatchResolveCache.delete(oldestKey);
    }
}

function buildPageShiftBatchResolveCacheKey(options: {
    readonly userId: string | null;
    readonly organizationId: string;
    readonly provider: Provider;
    readonly sourceUrl: string;
    readonly externalShiftIds: ReadonlyArray<string>;
    readonly wellskyShiftUniqueIds: ReadonlyArray<string | null>;
}): string {
    // Sort paired (externalShiftId, uniqueId) tuples together so the
    // cache key is stable regardless of input ordering.
    const pairs = options.externalShiftIds.map(
        (id, i) => [id, options.wellskyShiftUniqueIds[i] ?? null] as const,
    );
    pairs.sort((a, b) => {
        const cmp = a[0].localeCompare(b[0]);
        return cmp !== 0 ? cmp : (a[1] ?? '').localeCompare(b[1] ?? '');
    });
    return JSON.stringify([
        options.userId ?? 'none',
        options.organizationId,
        options.provider,
        options.sourceUrl,
        pairs,
    ]);
}

function getCachedPageShiftBatchResolveItems(
    cacheKey: string,
): ReadonlyArray<BatchResolveShiftResponseItem> | null {
    const cached = pageShiftBatchResolveCache.get(cacheKey);
    if (!cached) {
        return null;
    }

    if (cached.expiresAtMs <= Date.now()) {
        pageShiftBatchResolveCache.delete(cacheKey);
        return null;
    }

    pageShiftBatchResolveCache.delete(cacheKey);
    pageShiftBatchResolveCache.set(cacheKey, cached);
    return cached.items;
}

function setCachedPageShiftBatchResolveItems(
    cacheKey: string,
    items: ReadonlyArray<BatchResolveShiftResponseItem>,
): ReadonlyArray<BatchResolveShiftResponseItem> {
    pageShiftBatchResolveCache.set(cacheKey, {
        expiresAtMs: Date.now() + PAGE_SHIFT_BATCH_RESOLVE_CACHE_TTL_MS,
        items,
    });
    prunePageShiftBatchResolveCache();
    return items;
}

async function resolvePageShiftBatchItems(options: {
    readonly userId: string | null;
    readonly organizationId: string;
    readonly provider: Provider;
    readonly sourceUrl: string;
    readonly externalShiftIds: ReadonlyArray<string>;
    readonly wellskyShiftUniqueIds: ReadonlyArray<string | null>;
}): Promise<ReadonlyArray<BatchResolveShiftResponseItem>> {
    // Dedup by composite key (externalShiftId + wellskyShiftUniqueId) so
    // recurring WellSky shifts with different dates are preserved as
    // distinct entries rather than collapsed into one.
    const seen = new Set<string>();
    const dedupedPairs: Array<{
        externalShiftId: string;
        uniqueId: string | null;
    }> = [];
    for (let i = 0; i < options.externalShiftIds.length; i++) {
        const trimmed = options.externalShiftIds[i]?.trim() ?? '';
        if (trimmed.length === 0) {
            continue;
        }
        const uniqueId = options.wellskyShiftUniqueIds[i] ?? null;
        const compositeKey = uniqueId ? `${trimmed}::${uniqueId}` : trimmed;
        if (!seen.has(compositeKey)) {
            seen.add(compositeKey);
            dedupedPairs.push({ externalShiftId: trimmed, uniqueId });
        }
    }
    if (dedupedPairs.length === 0) {
        return [];
    }

    const externalShiftIds = dedupedPairs.map((p) => p.externalShiftId);
    const wellskyShiftUniqueIds = dedupedPairs.map((p) => p.uniqueId);

    const cacheKey = buildPageShiftBatchResolveCacheKey({
        ...options,
        externalShiftIds,
        wellskyShiftUniqueIds,
    });
    const cachedItems = getCachedPageShiftBatchResolveItems(cacheKey);
    if (cachedItems !== null) {
        return cachedItems;
    }

    const inflightRequest = pageShiftBatchResolveInflightRequests.get(cacheKey);
    if (inflightRequest) {
        return inflightRequest;
    }

    const externalShiftIdChunks = chunkReadonlyArray(
        externalShiftIds,
        MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS,
    );
    const wellskyShiftUniqueIdChunks = chunkReadonlyArray(
        wellskyShiftUniqueIds,
        MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS,
    );

    const nextRequest = Promise.all(
        externalShiftIdChunks.map((chunk, chunkIndex) => {
            const wellskyShiftUniqueIdChunk =
                wellskyShiftUniqueIdChunks[chunkIndex];
            const hasAnyUniqueId =
                wellskyShiftUniqueIdChunk?.some(
                    (uniqueId) => uniqueId !== null,
                ) ?? false;
            return getApiClient().post({
                url: '/organizations/{organization_id}/extension/batch-resolve-shifts',
                pathParams: {
                    organization_id: options.organizationId,
                },
                body: {
                    provider: options.provider,
                    external_shift_ids: [...chunk],
                    source_url: options.sourceUrl,
                    ...(hasAnyUniqueId
                        ? {
                              wellsky_shift_unique_ids: [
                                  ...(wellskyShiftUniqueIdChunk ?? []),
                              ],
                          }
                        : {}),
                },
            });
        }),
    )
        .then((responses) => {
            const items = responses.flatMap((response) => {
                const data = response as BatchResolveShiftsResponse;
                return data.items;
            });
            return setCachedPageShiftBatchResolveItems(cacheKey, items);
        })
        .finally(() => {
            if (
                pageShiftBatchResolveInflightRequests.get(cacheKey) ===
                nextRequest
            ) {
                pageShiftBatchResolveInflightRequests.delete(cacheKey);
            }
        });

    pageShiftBatchResolveInflightRequests.set(cacheKey, nextRequest);
    return nextRequest;
}

export function resetPageShiftBatchResolveCacheForTests(): void {
    pageShiftBatchResolveCache.clear();
    pageShiftBatchResolveInflightRequests.clear();
}

type OrganizationDomainCacheEntry = {
    readonly organizationId: string;
    readonly allowedDomains: ReadonlyArray<string>;
};

type OrganizationDomainCache = {
    readonly version: number;
    readonly updatedAtMs: number;
    readonly organizations: ReadonlyArray<OrganizationDomainCacheEntry>;
};

type ExtensionConfigsResponse = ApiResponseType<'/extension/configs', 'get'>;
type ExtensionSidepanelConfigPayload = {
    // Always optional. Old extension shells (pre-multi-shift) do not send
    // `EXTENSION_SIDEPANEL_CONFIG` at all; newer shells may omit individual
    // fields as we grow the payload over time. Feature gates below must
    // treat missing fields as "use the iframe's own fallback" rather than
    // blocking.
    readonly extensionId?: string;
    readonly extensionInstallType?: ChromeExtensionInstallType;
    readonly extensionVersion?: string;
    readonly syncSidebarOpenStateByDomain?: boolean;
};
type SelectedShiftResolutionState =
    | 'idle'
    | 'waiting_for_context'
    | 'resolving'
    | 'error'
    | 'ready';
type OrganizationConversationMetadata = ApiResponseType<
    '/organizations/{organization_id}/conversations/{conversation_id}/metadata',
    'get'
>;

const MINIMIZED_RAIL_STORAGE_KEY = 'phoebe-extension-minimized-rail-enabled';
const MINIMIZED_RAIL_MANUAL_STORAGE_KEY =
    'phoebe-extension-minimized-rail-manually-configured';
const ONLY_MINE_FEED_FILTER_STORAGE_KEY =
    'phoebe-extension-only-mine-feed-filter';
const FEED_SORT_STORAGE_KEY = 'phoebe-extension-feed-sort';
const DEVELOPMENT_BUILD_STORE_REMINDER_MAX_VERSION = '0.1.1';
const ORGANIZATION_DOMAIN_CACHE_STORAGE_KEY_BASE =
    'phoebe-extension-organization-domains';
const ORGANIZATION_DOMAIN_CACHE_TTL_MS = 5 * 60 * 1000;
const ORGANIZATION_DOMAIN_CACHE_VERSION = 1;
const AXISCARE_HOST_SUFFIX = '.axiscare.com';
const AXISCARE_ROOT_HOSTNAME = 'axiscare.com';

function readLocalStorageBoolean(key: string, defaultValue: boolean): boolean {
    try {
        const stored = localStorage.getItem(key);
        if (stored === null) {
            return defaultValue;
        }
        return stored === 'true';
    } catch {
        return defaultValue;
    }
}

function parseLocalStorageBoolean(value: string | null): boolean | null {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return null;
}

function readMinimizedRailPreference(userId: string): {
    readonly enabled: boolean;
    readonly manuallyConfigured: boolean;
} {
    const storageKey = `${MINIMIZED_RAIL_STORAGE_KEY}:${userId}`;
    const manualStorageKey = `${MINIMIZED_RAIL_MANUAL_STORAGE_KEY}:${userId}`;
    try {
        const storedValue = parseLocalStorageBoolean(
            localStorage.getItem(storageKey),
        );
        const isManual =
            parseLocalStorageBoolean(localStorage.getItem(manualStorageKey)) ===
            true;

        if (isManual && storedValue !== null) {
            return {
                enabled: storedValue,
                manuallyConfigured: true,
            };
        }

        if (storedValue === false) {
            return {
                enabled: false,
                manuallyConfigured: true,
            };
        }
    } catch {
        // localStorage may be unavailable
    }

    return {
        enabled: EXTENSION_MINIMIZED_RAIL_DEFAULT_ENABLED,
        manuallyConfigured: false,
    };
}

function isAllowedOrigin(origin: string): boolean {
    return (
        origin === window.location.origin ||
        origin.endsWith('.clearcareonline.com') ||
        origin.endsWith('.axiscare.com') ||
        origin.endsWith('.hhaexchange.com') ||
        origin.startsWith('chrome-extension://')
    );
}

function buildPageInfoKey(pageInfo: PageInfo | null): string {
    if (pageInfo === null) {
        return 'none';
    }

    return [
        pageInfo.provider ?? 'none',
        pageInfo.entityType ?? 'none',
        pageInfo.externalId ?? 'none',
        pageInfo.url,
    ].join('|');
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function getSidepanelExtensionVersion(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }

    const nextValue = value['extensionVersion'];
    return typeof nextValue === 'string' && nextValue.length > 0
        ? nextValue
        : null;
}

function getSidepanelExtensionId(value: unknown): string | null {
    if (!isRecord(value)) {
        return null;
    }

    const nextValue = value['extensionId'];
    return typeof nextValue === 'string' && nextValue.length > 0
        ? nextValue
        : null;
}

function getSidepanelExtensionInstallType(
    value: unknown,
): ChromeExtensionInstallType | null {
    if (!isRecord(value)) {
        return null;
    }

    const nextValue = value['extensionInstallType'];
    return isChromeExtensionInstallType(nextValue) ? nextValue : null;
}

function getSidepanelSyncSidebarOpenStateByDomain(
    value: unknown,
): boolean | null {
    if (!isRecord(value)) {
        return null;
    }

    const nextValue = value['syncSidebarOpenStateByDomain'];
    return typeof nextValue === 'boolean' ? nextValue : null;
}

function getReferrerChromeExtensionId(referrer: string): string | null {
    try {
        const referrerUrl = new URL(referrer);
        return referrerUrl.protocol === 'chrome-extension:'
            ? referrerUrl.hostname
            : null;
    } catch {
        return null;
    }
}

function compareExtensionVersions(left: string, right: string): number {
    const leftParts = left.split('.').map((part) => Number.parseInt(part, 10));
    const rightParts = right
        .split('.')
        .map((part) => Number.parseInt(part, 10));
    const length = Math.max(leftParts.length, rightParts.length);

    for (let index = 0; index < length; index += 1) {
        const leftPart = leftParts[index] ?? 0;
        const rightPart = rightParts[index] ?? 0;
        if (!Number.isFinite(leftPart) || !Number.isFinite(rightPart)) {
            return left.localeCompare(right);
        }
        if (leftPart !== rightPart) {
            return leftPart - rightPart;
        }
    }

    return 0;
}

function shouldShowDevelopmentBuildStoreReminder(options: {
    readonly extensionId: string | null;
    readonly extensionInstallType: ChromeExtensionInstallType | null;
    readonly extensionVersion: string | null;
    readonly referrer: string;
}): boolean {
    const { extensionInstallType, extensionVersion } = options;
    if (
        extensionVersion === null ||
        compareExtensionVersions(
            extensionVersion,
            DEVELOPMENT_BUILD_STORE_REMINDER_MAX_VERSION,
        ) > 0
    ) {
        return false;
    }

    if (extensionInstallType !== null) {
        return (
            extensionInstallType === 'development' ||
            extensionInstallType === 'sideload'
        );
    }

    const extensionId =
        options.extensionId ?? getReferrerChromeExtensionId(options.referrer);
    return extensionId !== null && extensionId !== PHOEBE_CHROME_EXTENSION_ID;
}

function isPageInfoPayload(value: unknown): value is PageInfo {
    if (!isRecord(value) || typeof value['url'] !== 'string') {
        return false;
    }

    if (value['provider'] !== null && !isProvider(value['provider'])) {
        return false;
    }

    if (
        value['entityType'] !== undefined &&
        value['entityType'] !== 'shifts' &&
        value['entityType'] !== 'caregivers' &&
        value['entityType'] !== 'clients' &&
        value['entityType'] !== 'care_coordinators'
    ) {
        return false;
    }

    return (
        value['externalId'] === undefined ||
        typeof value['externalId'] === 'string'
    );
}

function isExtensionPageShiftPayload(
    value: unknown,
): value is ExtensionPageShift {
    if (!isRecord(value)) {
        return false;
    }

    if (!isProvider(value['provider'])) {
        return false;
    }

    if (
        typeof value['externalShiftId'] !== 'string' ||
        typeof value['sourceUrl'] !== 'string' ||
        typeof value['label'] !== 'string' ||
        typeof value['title'] !== 'string'
    ) {
        return false;
    }

    if (
        value['variant'] !== 'fill' &&
        value['variant'] !== 'assign' &&
        value['variant'] !== 'open'
    ) {
        return false;
    }

    return (
        (value['activeOutreachId'] === null ||
            typeof value['activeOutreachId'] === 'string') &&
        (value['matchKey'] === undefined ||
            typeof value['matchKey'] === 'string') &&
        (value['wellskyShiftUniqueId'] === undefined ||
            typeof value['wellskyShiftUniqueId'] === 'string') &&
        (value['subtitle'] === undefined ||
            typeof value['subtitle'] === 'string') &&
        (value['details'] === undefined || typeof value['details'] === 'string')
    );
}

const REALTIME_REFRESH_DELAY_MS = 3_000;
const CLIENT_SHIFT_TIME_REFRESH_MS = 60_000;
const CLIENT_OUTREACH_SELECTION_THRESHOLD = 10;
const CLIENT_SHIFT_LOOKAHEAD_DAYS = 14;
const CLIENT_SHIFT_SEARCH_LIMIT = 1_000;
const ACTIVE_SHIFT_OUTREACH_STATUSES: Array<OutreachStatus> = [
    'pending',
    'in_progress',
    'paused',
];
const ACTIVE_SHIFT_OUTREACH_TYPES = ['callout', 'recurring_shift'] as const;
const BOTTOM_BAR_OUTREACH_STATUSES: Array<OutreachStatus> = [
    'pending',
    'in_progress',
];
const EXTENSION_AUTH_REFRESH_REQUEST = 'EXTENSION_AUTH_REFRESH_REQUEST';
const EXTENSION_CARD_CLASS =
    'rounded-2xl border border-border/80 bg-background ' +
    'shadow-[0_1px_2px_rgba(15,23,42,0.05),0_12px_28px_rgba(15,23,42,0.04)]';
const EXTENSION_CONTEXT_CARD_CLASS = `${EXTENSION_CARD_CLASS} p-4`;
const EXTENSION_SUBTLE_PANEL_CLASS =
    'rounded-2xl border border-border/80 bg-muted/70 p-4';
const EXTENSION_EYEBROW_CLASS = 'text-sm font-medium text-muted-foreground';
const EXTENSION_META_BADGE_CLASS =
    'rounded-full border border-border bg-muted/50 px-2.5 py-1 text-[11px] font-medium text-foreground/80';
const EXTENSION_SELECTED_CARD_CLASS =
    'border-indigo-200 bg-indigo-50/70 dark:border-indigo-900 dark:bg-indigo-950/50';
const EXTENSION_PRIMARY_ACTION_BUTTON_CLASS =
    'bg-primary text-primary-foreground hover:bg-primary/90';
const EXTENSION_SELECTED_ACTION_BUTTON_CLASS =
    'bg-[#5533ee] text-white hover:bg-[#4826d9]';

function getUserName(user: User): string {
    return [user.first_name, user.last_name].filter(Boolean).join(' ');
}

function getResolvablePageInfo(
    pageInfo: PageInfo | null,
): ResolvablePageInfo | null {
    if (
        !pageInfo?.provider ||
        (pageInfo.entityType !== 'shifts' &&
            pageInfo.entityType !== 'caregivers' &&
            pageInfo.entityType !== 'clients') ||
        typeof pageInfo.externalId !== 'string' ||
        pageInfo.externalId.length === 0
    ) {
        return null;
    }

    return {
        ...pageInfo,
        provider: pageInfo.provider,
        entityType: pageInfo.entityType,
        externalId: pageInfo.externalId,
    };
}

function isAxisCareClientPageInfo(
    pageInfo: PageInfo | null,
): pageInfo is ResolvablePageInfo & {
    readonly provider: 'axiscare';
    readonly entityType: 'clients';
} {
    const resolvablePageInfo = getResolvablePageInfo(pageInfo);
    return (
        resolvablePageInfo?.provider === 'axiscare' &&
        resolvablePageInfo.entityType === 'clients'
    );
}

function buildResolveContextRequest(
    pageInfo: PageInfo | null,
): ExtensionResolveContextRequest | null {
    const resolvablePageInfo = getResolvablePageInfo(pageInfo);
    if (!resolvablePageInfo) {
        return null;
    }

    return {
        provider: resolvablePageInfo.provider,
        entity_type: resolvablePageInfo.entityType,
        external_id: resolvablePageInfo.externalId,
        source_url: resolvablePageInfo.url,
    };
}

function buildOrganizationDomainCacheStorageKey(
    userId: string | null,
): string | null {
    return userId
        ? `${ORGANIZATION_DOMAIN_CACHE_STORAGE_KEY_BASE}:${userId}`
        : null;
}

function normalizeHostname(hostname: string): string {
    return hostname.trim().toLowerCase();
}

function normalizeAllowedDomains(
    allowedDomains: ReadonlyArray<string>,
): Array<string> {
    return [...new Set(allowedDomains.map(normalizeHostname).filter(Boolean))];
}

function getAxisCareInstanceHostname(pageInfo: PageInfo | null): string | null {
    if (pageInfo?.provider !== 'axiscare') {
        return null;
    }

    try {
        const parsedUrl = new URL(pageInfo.url);
        const hostname = normalizeHostname(parsedUrl.hostname);
        if (
            hostname === 'localhost' ||
            hostname === '127.0.0.1' ||
            hostname === AXISCARE_ROOT_HOSTNAME ||
            !hostname.endsWith(AXISCARE_HOST_SUFFIX)
        ) {
            return null;
        }

        return hostname;
    } catch {
        return null;
    }
}

export function buildFeedShiftActionUpdate(
    item: HomeFeedItem,
    provider: Provider | null,
    sourceUrl: string | null,
    timezone: string,
): FeedShiftActionUpdate | null {
    const shift = item.shift;
    if (
        provider !== 'axiscare' ||
        !shift ||
        shift.status === 'cancelled' ||
        sourceUrl === null
    ) {
        return null;
    }

    const matchKey = buildAxisCareShiftMatchKey({
        patientName: shift.patient_name,
        shiftStartTime: shift.shift_start_time,
        shiftEndTime: shift.shift_end_time,
        timezone,
    });
    if (matchKey === null) {
        return null;
    }

    if (item.display_status === 'awaiting_confirmation') {
        return {
            provider,
            label: 'Assign with Phoebe',
            matchKey,
            outreachId: item.id,
            sourceUrl,
            variant: 'assign',
        };
    }

    return {
        provider,
        label: 'View outreach',
        matchKey,
        outreachId: item.id,
        sourceUrl,
        variant: 'open',
    };
}

function normalizeShiftMatchName(name: string): string {
    return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

function formatAxisCareMatchDate(
    dateTime: string,
    timezone: string,
): string | null {
    const parsedDate = new Date(dateTime);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat('en-CA', {
        day: '2-digit',
        month: '2-digit',
        timeZone: timezone,
        year: 'numeric',
    }).format(parsedDate);
}

function formatAxisCareMatchTime(
    dateTime: string,
    timezone: string,
): string | null {
    const parsedDate = new Date(dateTime);
    if (Number.isNaN(parsedDate.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        hour12: true,
        minute: '2-digit',
        timeZone: timezone,
    })
        .format(parsedDate)
        .toLowerCase()
        .replace(':00', '')
        .replace(/\s+/g, '')
        .replace('am', 'a')
        .replace('pm', 'p');
}

function buildAxisCareShiftMatchKey(options: {
    readonly patientName: string;
    readonly shiftStartTime: string;
    readonly shiftEndTime: string;
    readonly timezone: string;
}): string | null {
    const visitDate = formatAxisCareMatchDate(
        options.shiftStartTime,
        options.timezone,
    );
    const startTime = formatAxisCareMatchTime(
        options.shiftStartTime,
        options.timezone,
    );
    const endTime = formatAxisCareMatchTime(
        options.shiftEndTime,
        options.timezone,
    );

    if (visitDate === null || startTime === null || endTime === null) {
        return null;
    }

    return [
        normalizeShiftMatchName(options.patientName),
        visitDate,
        startTime,
        endTime,
    ].join('|');
}

function postShiftActionUpdate(update: FeedShiftActionUpdate) {
    window.parent.postMessage(
        {
            type: 'EXTENSION_SELECTED_SHIFT_ACTION_UPDATE',
            payload: {
                provider: update.provider,
                externalShiftId: update.externalShiftId,
                label: update.label,
                matchKey: update.matchKey,
                outreachId: update.outreachId,
                sourceUrl: update.sourceUrl,
                variant: update.variant,
            },
        },
        '*',
    );
}

function readOrganizationDomainCache(
    storageKey: string | null,
): OrganizationDomainCache | null {
    if (!storageKey) {
        return null;
    }

    try {
        const rawValue = localStorage.getItem(storageKey);
        if (!rawValue) {
            return null;
        }

        const parsedValue = JSON.parse(
            rawValue,
        ) as Partial<OrganizationDomainCache>;
        if (
            parsedValue.version !== ORGANIZATION_DOMAIN_CACHE_VERSION ||
            typeof parsedValue.updatedAtMs !== 'number' ||
            !Array.isArray(parsedValue.organizations)
        ) {
            return null;
        }

        const organizations = parsedValue.organizations.flatMap((entry) => {
            if (
                !entry ||
                typeof entry.organizationId !== 'string' ||
                !Array.isArray(entry.allowedDomains)
            ) {
                return [];
            }

            return [
                {
                    organizationId: entry.organizationId,
                    allowedDomains: normalizeAllowedDomains(
                        entry.allowedDomains,
                    ),
                },
            ];
        });

        return {
            version: ORGANIZATION_DOMAIN_CACHE_VERSION,
            updatedAtMs: parsedValue.updatedAtMs,
            organizations,
        };
    } catch {
        return null;
    }
}

function writeOrganizationDomainCache(
    storageKey: string | null,
    cache: OrganizationDomainCache,
): void {
    if (!storageKey) {
        return;
    }

    try {
        localStorage.setItem(storageKey, JSON.stringify(cache));
    } catch {
        // Ignore local storage failures in private browsing or restricted contexts.
    }
}

function hasFreshOrganizationDomainCache(
    cache: OrganizationDomainCache | null,
    memberships: ReadonlyArray<{ readonly organization_id: string }>,
): boolean {
    if (cache === null) {
        return false;
    }

    if (Date.now() - cache.updatedAtMs > ORGANIZATION_DOMAIN_CACHE_TTL_MS) {
        return false;
    }

    const cachedOrganizationIds = new Set(
        cache.organizations.map((entry) => entry.organizationId),
    );

    return memberships.every((membership) =>
        cachedOrganizationIds.has(membership.organization_id),
    );
}

function findMatchingOrganizationIdForHostname(
    hostname: string | null,
    cache: OrganizationDomainCache | null,
    memberships: ReadonlyArray<{ readonly organization_id: string }>,
): string | null {
    if (hostname === null || cache === null) {
        return null;
    }

    const membershipIds = new Set(
        memberships.map((membership) => membership.organization_id),
    );
    const matchingOrganizationIds = cache.organizations
        .filter(
            (entry) =>
                membershipIds.has(entry.organizationId) &&
                entry.allowedDomains.some((domain) => hostname === domain),
        )
        .map((entry) => entry.organizationId);

    return matchingOrganizationIds.length === 1
        ? (matchingOrganizationIds[0] ?? null)
        : null;
}

function formatShiftStatusLabel(status: Shift['status']): string {
    const label = status.replace(/_/g, ' ');
    return label.charAt(0).toUpperCase() + label.slice(1);
}

function canAssignFromOutreach(
    outreach: {
        readonly display_status?: string | null;
    } | null,
): boolean {
    return outreach?.display_status === 'awaiting_confirmation';
}

function normalizeClientName(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ');
}

function filterOutreachesForClient(
    homeFeedItems: ReadonlyArray<HomeFeedItem>,
    clientName: string,
): Array<HomeFeedItem> {
    const normalizedClientName = normalizeClientName(clientName);
    if (normalizedClientName.length === 0) {
        return [];
    }

    return homeFeedItems.filter((item) => {
        const patientName = item.shift?.patient_name;
        if (!patientName) {
            return false;
        }

        return normalizeClientName(patientName) === normalizedClientName;
    });
}

function filterShiftsForClient(
    shifts: ReadonlyArray<Shift>,
    clientId: string | null,
    clientName: string | null,
): Array<Shift> {
    const normalizedClientName =
        clientName !== null ? normalizeClientName(clientName) : '';

    return shifts.filter((shift) => {
        if (clientId !== null) {
            if (shift.client_id === clientId) {
                return true;
            }

            if (shift.client_id !== null) {
                return false;
            }
        }

        if (normalizedClientName.length === 0) {
            return false;
        }

        return normalizeClientName(shift.patient_name) === normalizedClientName;
    });
}

function buildCreateOutreachUrl(
    shiftIds: ReadonlyArray<string>,
    organizationId?: string | null,
    mode?: 'live' | 'sandbox' | null,
): string {
    const searchParams = new URLSearchParams({
        openCreateOutreach: '1',
        source: 'extension',
        shiftIds: shiftIds.join(','),
    });
    if (organizationId) {
        searchParams.set('organizationId', organizationId);
    }
    if (mode) {
        searchParams.set('mode', mode);
    }

    return `/outreaches?${searchParams.toString()}`;
}

function buildOutreachDetailUrl(
    outreachId: string,
    organizationId?: string | null,
    mode?: 'live' | 'sandbox' | null,
): string {
    const searchParams = new URLSearchParams();
    if (organizationId) {
        searchParams.set('organizationId', organizationId);
    }
    if (mode) {
        searchParams.set('mode', mode);
    }

    const search = searchParams.toString();
    return `/outreaches/${outreachId}${search ? `?${search}` : ''}`;
}

function buildConversationDetailUrl(
    conversationId: string,
    organizationId?: string | null,
    mode?: 'live' | 'sandbox' | null,
): string {
    const searchParams = new URLSearchParams({
        conversationId,
    });
    if (organizationId) {
        searchParams.set('organizationId', organizationId);
    }
    if (mode) {
        searchParams.set('mode', mode);
    }

    return `/conversations?${searchParams.toString()}`;
}

function describeUnknownError(error: unknown): string | null {
    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return null;
}

function logExtensionPageEvent(
    message: string,
    details?: Record<string, unknown>,
) {
    if (details) {
        console.debug(`[Phoebe][ExtensionPage] ${message}`, details);
        return;
    }

    console.debug(`[Phoebe][ExtensionPage] ${message}`);
}

function ExtensionLoadingState() {
    return (
        <div className="bg-muted/20 flex min-h-screen items-center justify-center p-4">
            <div className="bg-background text-muted-foreground flex items-center gap-2 rounded-xl border px-4 py-3 text-sm shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading Phoebe…</span>
            </div>
        </div>
    );
}

function requestExtensionAuthRefresh() {
    window.parent.postMessage({ type: EXTENSION_AUTH_REFRESH_REQUEST }, '*');
}

function ExtensionSignInState() {
    const [hasOpenedSignInTab, setHasOpenedSignInTab] = useState(false);

    function openSignInTab() {
        const signInWindow = window.open(
            '/sign-in',
            '_blank',
            'noopener,noreferrer',
        );

        if (signInWindow !== null) {
            setHasOpenedSignInTab(true);
        }
    }

    return (
        <div className="bg-muted/20 flex min-h-screen items-center justify-center p-4">
            <div className="bg-background w-full max-w-sm space-y-4 rounded-[24px] border p-5 shadow-sm">
                <div className="space-y-2">
                    <h1 className="text-base font-semibold text-balance">
                        Sign in to Phoebe
                    </h1>
                    <p className="text-muted-foreground text-sm leading-6">
                        Complete sign in in a Phoebe browser tab, then refresh
                        the sidebar session here.
                    </p>
                </div>
                <div className="grid gap-2">
                    <Button onClick={openSignInTab}>
                        {hasOpenedSignInTab
                            ? 'Open sign-in again'
                            : 'Open sign-in tab'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={requestExtensionAuthRefresh}
                    >
                        Refresh sidebar session
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ExtensionPage() {
    const { initialized, session } = useSessionState();

    useEffect(() => {
        logExtensionPageEvent('Extension gate state changed', {
            initialized,
            userId: session?.identity.id ?? null,
        });
    }, [initialized, session?.identity.id]);

    useEffect(() => {
        const handleWindowError = (event: ErrorEvent) => {
            console.error('[Phoebe][ExtensionPage] Window error', {
                message: event.message,
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
                error: event.error,
            });
        };
        const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
            console.error(
                '[Phoebe][ExtensionPage] Unhandled promise rejection',
                {
                    reason: event.reason,
                },
            );
        };

        window.addEventListener('error', handleWindowError);
        window.addEventListener('unhandledrejection', handleUnhandledRejection);

        return () => {
            window.removeEventListener('error', handleWindowError);
            window.removeEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            );
        };
    }, []);

    if (!initialized) {
        return <ExtensionLoadingState />;
    }

    if (!session) {
        return <ExtensionSignInState />;
    }

    return (
        <ModeOverrideProvider mode="live">
            <AuthenticatedExtensionPage />
        </ModeOverrideProvider>
    );
}

function AuthenticatedExtensionPage() {
    const posthog = usePostHog();
    const { timezone } = useOrganizationTimezone();
    const { data: user, isLoading: isLoadingUser } = useUser({
        reason: 'extension-sidebar',
    });
    const {
        organizationId: selectedOrganizationId,
        memberships,
        isLoading: isLoadingOrganizationContext,
    } = useOrganizationContext();
    const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);
    const [organizationDomainCache, setOrganizationDomainCache] =
        useState<OrganizationDomainCache | null>(null);
    const hasTrackedSidebarOpenRef = useRef(false);
    const hasTrackedFeedViewedRef = useRef(false);
    const lastTrackedShiftContextRef = useRef<string | null>(null);
    const lastTrackedShiftContextOutreachRef = useRef<string | null>(null);
    const lastTrackedShiftContextNoOutreachRef = useRef<string | null>(null);
    const lastTrackedAxisCareClientRef = useRef<string | null>(null);
    const previousClientEligibleShiftIdsRef = useRef<Array<string>>([]);
    const lastPostedOutreachCountKeyRef = useRef<string | null>(null);
    const lastHandledCaregiverConversationContextKeyRef = useRef<string | null>(
        null,
    );
    const lastAutoFocusedPageShiftKeyRef = useRef<string | null>(null);
    const lastObservedPageInfoKeyRef = useRef<string>('none');
    const shouldRestoreDefaultTabRef = useRef(false);
    const isConversationWorkspacePinnedByUserRef = useRef(false);
    const selectedConversationSourceRef = useRef<
        'auto_context' | 'manual' | null
    >(null);
    const [currentTimeMs, setCurrentTimeMs] = useState(() => Date.now());
    const [selectedClientShiftIds, setSelectedClientShiftIds] = useState<
        Array<string>
    >([]);
    const [ehrSelectedShifts, setEhrSelectedShifts] = useState<
        Array<SelectedExtensionShift>
    >([]);
    const [pageShifts, setPageShifts] = useState<Array<ExtensionPageShift>>([]);
    const [pageShiftResolvedMap, setPageShiftResolvedMap] = useState<
        Map<string, BatchResolvedItem>
    >(() => new Map());
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [organizationSearch, setOrganizationSearch] = useState('');
    const [openOrganizationPickerId, setOpenOrganizationPickerId] = useState<
        string | null
    >(null);
    const [activeTab, setActiveTab] =
        useState<ExtensionSidebarTab>('outreaches');
    const [
        extensionShiftRangeRefreshSignal,
        setExtensionShiftRangeRefreshSignal,
    ] = useState(0);
    const [conversationSearch, setConversationSearch] = useState('');
    const [conversationPendingFirst, setConversationPendingFirst] =
        useState(false);
    const [selectedConversationId, setSelectedConversationId] = useState<
        string | null
    >(null);
    const [
        isMarkingAllConversationsAsRead,
        setIsMarkingAllConversationsAsRead,
    ] = useState(false);
    // Feed prefs (sort, "Created by me" filter) are scoped per user so a
    // different coordinator signing into the same browser profile doesn't
    // inherit the previous user's settings — that was especially
    // problematic for "Created by me" which could make the feed look
    // unexpectedly empty to the new user.
    //
    // Both prefs start at their defaults and hydrate from
    // `localStorage[<key>:<userId>]` once the user loads. The
    // `hydratedFeedPrefUserId` **state** (not a ref) gates the persist
    // effects so React batches it with the `setFeedSort` /
    // `setShowOnlyMineFeedFilter` calls from the hydration effect —
    // meaning by the time the persist effects see
    // `hydratedFeedPrefUserId === feedPrefUserId`, `feedSort` and
    // `showOnlyMineFeedFilter` already reflect the stored values. A
    // ref-based guard would flip on the same commit as hydration,
    // causing the persist effects to run with the still-default
    // values and overwrite the user's stored preferences with
    // defaults (self-corrects on the next render but a permanent
    // loss if the iframe unmounts in between).
    const [feedSort, setFeedSort] = useState<ExtensionOutreachFeedSort>(
        DEFAULT_EXTENSION_OUTREACH_FEED_SORT,
    );
    const [showOnlyMineFeedFilter, setShowOnlyMineFeedFilter] = useState(false);
    const [hydratedFeedPrefUserId, setHydratedFeedPrefUserId] = useState<
        string | null
    >(null);
    const [
        hydratedMinimizedRailPrefUserId,
        setHydratedMinimizedRailPrefUserId,
    ] = useState<string | null>(null);
    const [
        hasManualMinimizedRailPreference,
        setHasManualMinimizedRailPreference,
    ] = useState(false);
    const [
        hasHydratedSidebarOpenStateSync,
        setHasHydratedSidebarOpenStateSync,
    ] = useState(false);
    const extensionPrefUserId = user?.id ?? null;
    const feedPrefUserId = extensionPrefUserId;
    useEffect(() => {
        if (!feedPrefUserId || hydratedFeedPrefUserId === feedPrefUserId) {
            return;
        }
        try {
            const storedSort = localStorage.getItem(
                `${FEED_SORT_STORAGE_KEY}:${feedPrefUserId}`,
            );
            if (
                storedSort !== null &&
                isExtensionOutreachFeedSort(storedSort)
            ) {
                setFeedSort(storedSort);
            } else {
                setFeedSort(DEFAULT_EXTENSION_OUTREACH_FEED_SORT);
            }
            setShowOnlyMineFeedFilter(
                readLocalStorageBoolean(
                    `${ONLY_MINE_FEED_FILTER_STORAGE_KEY}:${feedPrefUserId}`,
                    false,
                ),
            );
        } catch {
            // localStorage may be unavailable
        }
        setHydratedFeedPrefUserId(feedPrefUserId);
    }, [feedPrefUserId, hydratedFeedPrefUserId]);
    useEffect(() => {
        if (!feedPrefUserId || hydratedFeedPrefUserId !== feedPrefUserId) {
            return;
        }
        try {
            localStorage.setItem(
                `${FEED_SORT_STORAGE_KEY}:${feedPrefUserId}`,
                feedSort,
            );
        } catch {
            // localStorage may be unavailable
        }
    }, [feedSort, feedPrefUserId, hydratedFeedPrefUserId]);
    useEffect(() => {
        if (!feedPrefUserId || hydratedFeedPrefUserId !== feedPrefUserId) {
            return;
        }
        try {
            localStorage.setItem(
                `${ONLY_MINE_FEED_FILTER_STORAGE_KEY}:${feedPrefUserId}`,
                String(showOnlyMineFeedFilter),
            );
        } catch {
            // localStorage may be unavailable
        }
    }, [showOnlyMineFeedFilter, feedPrefUserId, hydratedFeedPrefUserId]);
    const [isFeedControlsOpen, setIsFeedControlsOpen] = useState(false);
    const [showBottomBar, setShowBottomBar] = useState(
        EXTENSION_BOTTOM_BAR_ENABLED,
    );
    const [showMinimizedRail, setShowMinimizedRail] = useState(() => {
        if (!EXTENSION_MINIMIZED_RAIL_ENABLED) {
            return false;
        }

        return EXTENSION_MINIMIZED_RAIL_DEFAULT_ENABLED;
    });
    const [syncSidebarOpenStateByDomain, setSyncSidebarOpenStateByDomain] =
        useState(EXTENSION_SIDEBAR_OPEN_STATE_SYNC_DEFAULT_ENABLED);
    useEffect(() => {
        if (
            !EXTENSION_MINIMIZED_RAIL_ENABLED ||
            !extensionPrefUserId ||
            hydratedMinimizedRailPrefUserId === extensionPrefUserId
        ) {
            return;
        }

        const preference = readMinimizedRailPreference(extensionPrefUserId);
        setShowMinimizedRail(preference.enabled);
        setHasManualMinimizedRailPreference(preference.manuallyConfigured);
        setHydratedMinimizedRailPrefUserId(extensionPrefUserId);
    }, [extensionPrefUserId, hydratedMinimizedRailPrefUserId]);
    const organizationDomainCacheStorageKey = useMemo(
        () => buildOrganizationDomainCacheStorageKey(user?.id ?? null),
        [user?.id],
    );
    const extensionOrganizationStorageKey = useMemo(
        () => buildExtensionOrganizationStorageKey(user?.id ?? null),
        [user?.id],
    );
    const [
        extensionSelectedOrganizationId,
        setExtensionSelectedOrganizationId,
    ] = useState<string | null>(null);
    const [sidepanelConfig, setSidepanelConfig] =
        useState<ExtensionSidepanelConfigPayload | null>(null);

    useEffect(() => {
        const nextValue = sidepanelConfig?.syncSidebarOpenStateByDomain;
        if (nextValue === undefined) {
            return;
        }

        setSyncSidebarOpenStateByDomain(nextValue);
        setHasHydratedSidebarOpenStateSync(true);
    }, [sidepanelConfig?.syncSidebarOpenStateByDomain]);

    useEffect(() => {
        if (isLoadingUser || isLoadingOrganizationContext) {
            return;
        }

        if (!extensionOrganizationStorageKey || memberships.length === 0) {
            setExtensionSelectedOrganizationId(null);
            return;
        }

        setExtensionSelectedOrganizationId(
            readPersistedExtensionOrganizationId(
                extensionOrganizationStorageKey,
                memberships,
            ),
        );
    }, [
        extensionOrganizationStorageKey,
        isLoadingOrganizationContext,
        isLoadingUser,
        memberships,
    ]);

    useEffect(() => {
        if (
            isLoadingUser ||
            isLoadingOrganizationContext ||
            !extensionOrganizationStorageKey ||
            memberships.length === 0
        ) {
            return;
        }

        const resolvedMemberships = memberships;

        function handleStorage(event: StorageEvent) {
            if (event.key !== extensionOrganizationStorageKey) {
                return;
            }

            setExtensionSelectedOrganizationId(
                readPersistedExtensionOrganizationId(
                    extensionOrganizationStorageKey,
                    resolvedMemberships,
                ),
            );
        }

        window.addEventListener('storage', handleStorage);
        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [
        extensionOrganizationStorageKey,
        isLoadingOrganizationContext,
        isLoadingUser,
        memberships,
    ]);
    const axisCareInstanceHostname = useMemo(
        () => getAxisCareInstanceHostname(pageInfo),
        [pageInfo],
    );
    const matchedOrganizationId = useMemo(
        () =>
            findMatchingOrganizationIdForHostname(
                axisCareInstanceHostname,
                organizationDomainCache,
                memberships,
            ),
        [axisCareInstanceHostname, organizationDomainCache, memberships],
    );
    const fixtureOrganizationId = useMemo(
        () =>
            findFixtureOrganizationId({
                memberships,
                provider: pageInfo?.provider ?? null,
                sourceUrl: pageInfo?.url ?? null,
            }),
        [memberships, pageInfo?.provider, pageInfo?.url],
    );
    // Keep extension hostname matching local to the extension surface. In
    // Chrome the extension UI shares the app origin, so syncing this
    // auto-resolved organization into the shared OrganizationProvider would
    // overwrite the main app's persisted org selection in other tabs.
    const resolvedOrganizationId = useMemo(
        () =>
            resolveExtensionOrganizationId({
                fixtureOrganizationId,
                extensionSelectedOrganizationId,
                matchedOrganizationId,
                selectedOrganizationId,
                memberships,
            }),
        [
            extensionSelectedOrganizationId,
            fixtureOrganizationId,
            matchedOrganizationId,
            memberships,
            selectedOrganizationId,
        ],
    );
    const activeMembership = useMemo(() => {
        if (!resolvedOrganizationId) {
            return memberships[0];
        }

        return (
            memberships.find(
                (membership) =>
                    membership.organization_id === resolvedOrganizationId,
            ) ?? memberships[0]
        );
    }, [memberships, resolvedOrganizationId]);
    const organizationOptions = useMemo(() => {
        return buildOrganizationMembershipOptions(
            memberships,
            user?.admin === true,
        );
    }, [memberships, user?.admin]);
    const activeOrganizationOption = useMemo(() => {
        return (
            organizationOptions.find(
                (organization) =>
                    organization.organization_id === resolvedOrganizationId,
            ) ?? null
        );
    }, [organizationOptions, resolvedOrganizationId]);
    const showOrganizationPicker = memberships.length > 1;
    const activeOrganizationLabel =
        activeOrganizationOption?.displayName ||
        (activeMembership
            ? formatOrganizationMembershipDisplayName(
                  activeMembership,
                  user?.admin === true,
              )
            : null) ||
        'Select organization';
    const membershipOrganizationName =
        activeOrganizationOption?.organization_name?.trim() ||
        activeMembership?.organization_name?.trim() ||
        null;

    const handleSelectExtensionOrganization = useCallback(
        (organizationId: string) => {
            writePersistedExtensionOrganizationId(
                extensionOrganizationStorageKey,
                organizationId,
            );
            setExtensionSelectedOrganizationId(organizationId);
        },
        [extensionOrganizationStorageKey],
    );
    const renderOrganizationPicker = useCallback(
        ({
            pickerId,
            triggerClassName,
            triggerWidthClassName,
        }: {
            readonly pickerId: string;
            readonly triggerClassName: string;
            readonly triggerWidthClassName?: string;
        }) => {
            if (!showOrganizationPicker) {
                return null;
            }

            return (
                <Popover
                    open={openOrganizationPickerId === pickerId}
                    onOpenChange={(open: boolean) => {
                        setOpenOrganizationPickerId(open ? pickerId : null);
                        if (open) {
                            setOrganizationSearch('');
                        }
                    }}
                >
                    <PopoverTrigger asChild={true}>
                        <Button
                            variant="ghost"
                            className={cn(
                                'h-8 min-w-0 justify-between gap-2 px-2 font-medium',
                                triggerWidthClassName,
                                triggerClassName,
                            )}
                        >
                            <span className="min-w-0 flex-1 truncate text-left">
                                {activeOrganizationLabel}
                            </span>
                            <ChevronDown className="h-4 w-4 flex-none opacity-60" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="end"
                        className="w-[min(22rem,calc(100vw-2rem))] min-w-[240px] p-1"
                        side="bottom"
                        onOpenAutoFocus={(event) => {
                            if (!(event.target instanceof HTMLElement)) {
                                return;
                            }
                            const input = event.target.querySelector(
                                '[data-slot="command-input"]',
                            );
                            if (input instanceof HTMLInputElement) {
                                event.preventDefault();
                                input.focus();
                            }
                        }}
                    >
                        <OrganizationMembershipPickerContent
                            options={organizationOptions}
                            selectedOrganizationId={resolvedOrganizationId}
                            searchValue={organizationSearch}
                            onSearchValueChange={setOrganizationSearch}
                            onSelectOrganization={(organizationId) => {
                                handleSelectExtensionOrganization(
                                    organizationId,
                                );
                                setOpenOrganizationPickerId(null);
                            }}
                        />
                    </PopoverContent>
                </Popover>
            );
        },
        [
            activeOrganizationLabel,
            handleSelectExtensionOrganization,
            openOrganizationPickerId,
            organizationOptions,
            organizationSearch,
            resolvedOrganizationId,
            showOrganizationPicker,
        ],
    );

    useEffect(() => {
        logExtensionPageEvent('Authenticated extension page mounted');
        return () => {
            logExtensionPageEvent('Authenticated extension page unmounted');
        };
    }, []);

    useEffect(() => {
        setOrganizationDomainCache(
            readOrganizationDomainCache(organizationDomainCacheStorageKey),
        );
    }, [organizationDomainCacheStorageKey]);

    useEffect(() => {
        if (
            axisCareInstanceHostname === null ||
            memberships.length <= 1 ||
            !organizationDomainCacheStorageKey ||
            hasFreshOrganizationDomainCache(
                organizationDomainCache,
                memberships,
            )
        ) {
            return;
        }

        let isCancelled = false;

        async function refreshOrganizationDomains() {
            try {
                // One request resolves the allowed-domain map for every org
                // the user belongs to. Fetching it per org was an N+1 burst
                // for multi-org accounts (Sentry WEB-C2).
                const response = (await getApiClient().get({
                    url: '/extension/configs',
                    reason: 'extension-organization-domain-resolution',
                })) as ExtensionConfigsResponse;

                if (isCancelled) {
                    return;
                }

                // Key the cache on `memberships`, not the response: the
                // freshness check requires an entry for every membership org,
                // and the effect re-runs whenever the cache changes. If the
                // response omitted a membership org (e.g. access revoked since
                // memberships loaded), a response-keyed cache would never read
                // as fresh and the effect would refetch in a loop.
                const configByOrganizationId = new Map(
                    response.configs.map((config) => [
                        config.organization_id,
                        config,
                    ]),
                );
                const organizations = memberships.map((membership) => ({
                    organizationId: membership.organization_id,
                    allowedDomains: normalizeAllowedDomains(
                        configByOrganizationId.get(membership.organization_id)
                            ?.allowed_domains ?? [],
                    ),
                }));

                const nextCache: OrganizationDomainCache = {
                    version: ORGANIZATION_DOMAIN_CACHE_VERSION,
                    updatedAtMs: Date.now(),
                    organizations,
                };
                setOrganizationDomainCache(nextCache);
                writeOrganizationDomainCache(
                    organizationDomainCacheStorageKey,
                    nextCache,
                );
            } catch (error) {
                console.error(
                    'Failed to refresh extension organization domains',
                    error,
                );
            }
        }

        void refreshOrganizationDomains();

        return () => {
            isCancelled = true;
        };
    }, [
        axisCareInstanceHostname,
        memberships,
        organizationDomainCache,
        organizationDomainCacheStorageKey,
    ]);

    const {
        data: homeFeed,
        error: homeFeedError,
        isLoading: isLoadingHomeFeed,
        mutate: mutateHomeFeed,
    } = useHomeFeed(resolvedOrganizationId, {
        reason: 'extension-sidebar',
    });
    const { data: extensionConfig } = useExtensionConfig(
        resolvedOrganizationId,
        { reason: 'extension-config' },
    );
    const { data: organization } = useOrganization(resolvedOrganizationId, {
        reason: 'extension-page',
    });
    const resolvedOrganizationName =
        organization?.name?.trim() ||
        extensionConfig?.organization_name?.trim() ||
        membershipOrganizationName ||
        (activeMembership ? activeOrganizationLabel : null);
    const effectiveUseMultiShiftCalloutResponse = true;
    const isEffectiveUseMultiShiftCalloutResponseUnresolved = false;
    // `useFeatureFlag` always returns a boolean (defaults to false when
    // `organization?.features` is still undefined), so we can't
    // distinguish "loading" from "explicitly disabled" from its output
    // alone. Track that unresolved window separately so callers that want
    // to render optimistically while waiting can do so.
    //
    // Keep treating org-feature lookup errors as unresolved here. The
    // sidepanel should not silently disable multi-shift selection just
    // because an auxiliary org read failed; the content script can keep
    // showing the checkbox in a pending state until the next successful
    // config refresh.
    // Null on old extension shells that don't forward their manifest
    // version through `EXTENSION_SIDEPANEL_CONFIG`. Used to tag Sentry
    // captures so we can attribute issues to specific extension builds and
    // identify the long tail of out-of-date clients.
    const extensionVersion = sidepanelConfig?.extensionVersion ?? null;
    const extensionId = sidepanelConfig?.extensionId ?? null;
    const extensionInstallType = sidepanelConfig?.extensionInstallType ?? null;
    const shouldShowStoreReleaseReminder =
        shouldShowDevelopmentBuildStoreReminder({
            extensionId,
            extensionInstallType,
            extensionVersion,
            referrer: typeof document === 'undefined' ? '' : document.referrer,
        });
    useOrganizationEvents(resolvedOrganizationId, user?.id ?? null, {
        browserNotificationsEnabled: true,
        notificationClickBehavior: 'new-tab',
        telemetrySurface: 'extension',
    });

    useEffect(() => {
        logExtensionPageEvent('Organization resolution state changed', {
            isLoadingUser,
            isLoadingOrganizationContext,
            userId: user?.id ?? null,
            extensionSelectedOrganizationId,
            selectedOrganizationId,
            fixtureOrganizationId,
            matchedOrganizationId,
            resolvedOrganizationId,
            membershipCount: memberships.length,
            pageInfoProvider: pageInfo?.provider ?? null,
            pageInfoUrl: pageInfo?.url ?? null,
        });
    }, [
        extensionSelectedOrganizationId,
        fixtureOrganizationId,
        isLoadingOrganizationContext,
        isLoadingUser,
        matchedOrganizationId,
        memberships.length,
        pageInfo?.provider,
        pageInfo?.url,
        resolvedOrganizationId,
        selectedOrganizationId,
        user?.id,
    ]);

    useEffect(() => {
        logExtensionPageEvent('Home feed state changed', {
            isLoadingHomeFeed,
            outreachCount: homeFeed?.outreaches.length ?? null,
            error: describeUnknownError(homeFeedError),
        });
    }, [homeFeed?.outreaches.length, homeFeedError, isLoadingHomeFeed]);
    const activeBottomBarOutreaches = useMemo(() => {
        return (homeFeed?.outreaches ?? []).filter((item) =>
            BOTTOM_BAR_OUTREACH_STATUSES.includes(item.status),
        );
    }, [homeFeed?.outreaches]);
    const feedShiftActionUpdates = useMemo(() => {
        return (homeFeed?.outreaches ?? [])
            .map((item) =>
                buildFeedShiftActionUpdate(
                    item,
                    pageInfo?.provider ?? null,
                    pageInfo?.url ?? null,
                    timezone,
                ),
            )
            .filter((item) => item !== null)
            .sort((left, right) => {
                const leftKey = left.matchKey ?? left.externalShiftId ?? '';
                const rightKey = right.matchKey ?? right.externalShiftId ?? '';
                return leftKey.localeCompare(rightKey);
            });
    }, [homeFeed?.outreaches, pageInfo?.provider, pageInfo?.url, timezone]);

    const resolveContextRequest = useMemo(
        () => buildResolveContextRequest(pageInfo),
        [pageInfo],
    );
    const {
        data: resolvedContext,
        isLoading: isLoadingResolvedContext,
        error: resolveContextError,
        mutate: mutateResolvedContext,
    } = useExtensionResolveContext(
        resolvedOrganizationId,
        resolveContextRequest,
        {
            reason: 'extension-context',
        },
    );
    const { revalidateSession } = useSessionState();
    useEffect(() => {
        // A definitive 401 from resolve-context means the session is dead.
        // Re-validate so the provider signs the user out — this component then
        // unmounts behind the sign-in gate, which stops this surface from
        // re-firing resolve-context and 401-storming on an always-open EHR tab.
        if (resolveContextError?.status === 401) {
            revalidateSession();
        }
    }, [resolveContextError, revalidateSession]);
    const resolvedShiftId =
        resolveContextRequest?.entity_type === 'shifts' &&
        resolvedContext?.entity_type === 'shifts' &&
        resolvedContext.status === 'resolved'
            ? (resolvedContext.phoebe_object_id ?? null)
            : null;
    const resolvedClientName =
        resolveContextRequest?.entity_type === 'clients' &&
        resolvedContext?.entity_type === 'clients' &&
        resolvedContext.status === 'resolved'
            ? resolvedContext.object_name?.trim() || null
            : null;
    const resolvedClientId =
        resolveContextRequest?.entity_type === 'clients' &&
        resolvedContext?.entity_type === 'clients' &&
        resolvedContext.status === 'resolved'
            ? (resolvedContext.phoebe_object_id ?? null)
            : null;
    const resolvedCaregiverId =
        resolveContextRequest?.entity_type === 'caregivers' &&
        resolvedContext?.entity_type === 'caregivers' &&
        resolvedContext.status === 'resolved'
            ? (resolvedContext.phoebe_object_id ?? null)
            : null;

    // General agent chat surface inside the extension. Follows the same
    // lifecycle gate as the in-app general sidebar agent; only enabled-stage
    // orgs get it (an unset/loading stage stays disabled).
    const isExtensionAgentChatEnabled = isGeneralAgentLifecycleEligible(
        organization?.customer_lifecycle_stage,
    );
    // Resolved Phoebe shift ids for the coordinator's EHR multi-selection.
    // Hoisted above the selection-derived actions; the batch-resolve effects
    // further below populate it.
    const [batchResolvedItems, setBatchResolvedItems] = useState<
        Array<BatchResolvedItem>
    >([]);
    const [isBatchResolving, setIsBatchResolving] = useState(false);
    const agentChatBarLabel = 'Ask Phoebe';
    const [isAgentChatOpen, setIsAgentChatOpen] = useState(false);
    const [hasOpenedAgentChat, setHasOpenedAgentChat] = useState(false);
    const handleOpenAgentChat = () => {
        setHasOpenedAgentChat(true);
        setIsAgentChatOpen(true);
    };
    // The extension switches organizations per EHR domain. On a switch, close
    // the chat; the chat itself is keyed on the org id so it fully remounts.
    useEffect(() => {
        setIsAgentChatOpen(false);
    }, [resolvedOrganizationId]);

    const [batchResolveError, setBatchResolveError] = useState<string | null>(
        null,
    );
    const [batchResolveRetryNonce, setBatchResolveRetryNonce] = useState(0);

    useEffect(() => {
        if (
            !effectiveUseMultiShiftCalloutResponse ||
            ehrSelectedShifts.length === 0 ||
            !resolvedOrganizationId
        ) {
            setBatchResolvedItems([]);
            setIsBatchResolving(false);
            setBatchResolveError(null);
            return;
        }

        const provider = ehrSelectedShifts[0]?.provider;
        if (!provider) {
            setBatchResolvedItems([]);
            setIsBatchResolving(false);
            setBatchResolveError(
                'Phoebe could not determine which EHR these selected shifts came from.',
            );
            return;
        }

        const externalShiftIds = ehrSelectedShifts.map(
            (s) => s.externalShiftId,
        );
        // WellSky-only: parallel list of per-date composite keys for
        // recurring shifts. Index-paired with `externalShiftIds`; entries
        // are `null` for selections that do not have a unique-id hint
        // (AxisCare, HHAExchange, or non-calendar WellSky surfaces). The
        // backend resolver uses the hint to disambiguate per-date rows.
        const wellskyShiftUniqueIds = ehrSelectedShifts.map(
            (s) => s.wellskyShiftUniqueId ?? null,
        );
        const externalShiftIdChunks = chunkReadonlyArray(
            externalShiftIds,
            MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS,
        );
        const wellskyShiftUniqueIdChunks = chunkReadonlyArray(
            wellskyShiftUniqueIds,
            MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS,
        );
        const sourceUrl = ehrSelectedShifts[0]?.sourceUrl ?? '';

        // Log the exact request shape so coordinators reporting "this
        // shift is in Phoebe but the sidebar says it isn't" can share
        // DevTools output and we can pin the org / provider / source URL
        // / external IDs that the client actually sent, without
        // guessing. The companion response log below captures what the
        // server returned.
        logExtensionPageEvent('Batch resolve shifts request', {
            provider,
            resolvedOrganizationId,
            sourceUrl,
            externalShiftIdsCount: externalShiftIds.length,
            externalShiftIdsSample: externalShiftIds.slice(0, 10),
        });

        let cancelled = false;
        setBatchResolvedItems([]);
        setIsBatchResolving(true);
        setBatchResolveError(null);

        void (async () => {
            try {
                const responses = await Promise.all(
                    externalShiftIdChunks.map(
                        (externalShiftIdChunk, chunkIndex) => {
                            const wellskyShiftUniqueIdChunk =
                                wellskyShiftUniqueIdChunks[chunkIndex];
                            // Only send the parallel list when at least one
                            // entry is non-null. AxisCare / HHAExchange chunks
                            // skip the field entirely so the legacy resolver
                            // path is taken on the backend.
                            const hasAnyUniqueId =
                                wellskyShiftUniqueIdChunk?.some(
                                    (uniqueId) => uniqueId !== null,
                                ) ?? false;
                            return getApiClient().post({
                                url: '/organizations/{organization_id}/extension/batch-resolve-shifts',
                                pathParams: {
                                    organization_id: resolvedOrganizationId,
                                },
                                body: {
                                    provider,
                                    external_shift_ids: [
                                        ...externalShiftIdChunk,
                                    ],
                                    source_url: sourceUrl,
                                    ...(hasAnyUniqueId
                                        ? {
                                              wellsky_shift_unique_ids: [
                                                  ...(wellskyShiftUniqueIdChunk ??
                                                      []),
                                              ],
                                          }
                                        : {}),
                                },
                            });
                        },
                    ),
                );
                if (cancelled) {
                    return;
                }
                const allItems = responses.flatMap((response, chunkIndex) => {
                    const data = response as BatchResolveShiftsResponse;
                    const chunkStartIndex =
                        chunkIndex * MAX_EXTENSION_BATCH_RESOLVE_SHIFT_IDS;
                    return data.items.map((item, itemIndex) => {
                        const requestIndex = chunkStartIndex + itemIndex;
                        const wellskyShiftUniqueId =
                            wellskyShiftUniqueIds[requestIndex] ?? null;
                        return {
                            externalShiftId: item.external_shift_id,
                            wellskyShiftUniqueId,
                            status: item.status,
                            phoebeShiftId: item.phoebe_shift_id ?? null,
                            patientName: item.patient_name ?? null,
                            shiftStartTime: item.shift_start_time ?? null,
                            shiftEndTime: item.shift_end_time ?? null,
                            shiftStatus: item.shift_status ?? null,
                            otherOrgMatches: (item.other_org_matches ?? []).map(
                                (match) => ({
                                    organizationId: match.organization_id,
                                    organizationName: match.organization_name,
                                    phoebeShiftId: match.phoebe_shift_id,
                                    patientName: match.patient_name ?? null,
                                    shiftStartTime:
                                        match.shift_start_time ?? null,
                                    shiftEndTime: match.shift_end_time ?? null,
                                }),
                            ),
                        };
                    });
                });
                // Paired response log for the request log above. Counts
                // are bounded; a small sample carries the actual IDs the
                // server returned so we can compare against what the
                // caller saw in the UI.
                logExtensionPageEvent('Batch resolve shifts response', {
                    resolvedOrganizationId,
                    itemsCount: allItems.length,
                    statusCounts: allItems.reduce<Record<string, number>>(
                        (counts, item) => {
                            counts[item.status] =
                                (counts[item.status] ?? 0) + 1;
                            return counts;
                        },
                        {},
                    ),
                    sample: allItems.slice(0, 10).map((item) => ({
                        externalShiftId: item.externalShiftId,
                        status: item.status,
                        phoebeShiftId: item.phoebeShiftId,
                    })),
                });
                const resolvedItemsByIdentityKey = new Map(
                    allItems.map(
                        (item) => [buildShiftIdentityKey(item), item] as const,
                    ),
                );
                const nextResolvedItems = ehrSelectedShifts.map((shift) => {
                    const identityKey = buildShiftIdentityKey(shift);
                    return (
                        resolvedItemsByIdentityKey.get(identityKey) ?? {
                            externalShiftId: shift.externalShiftId,
                            wellskyShiftUniqueId:
                                shift.wellskyShiftUniqueId ?? null,
                            status: 'missing' as const,
                            phoebeShiftId: null,
                            patientName: null,
                            shiftStartTime: null,
                            shiftEndTime: null,
                            shiftStatus: null,
                            otherOrgMatches: [],
                        }
                    );
                });
                const missingExternalShiftIds = nextResolvedItems
                    .filter((item) => item.status === 'missing')
                    .map((item) => item.externalShiftId);

                if (missingExternalShiftIds.length > 0) {
                    logExtensionPageEvent(
                        'Batch resolve shifts returned incomplete results',
                        {
                            missingExternalShiftIds,
                            resolvedOrganizationId,
                        },
                    );
                }

                // Only `not_found` (truly unsynced in any org the user can
                // see) is a real friction signal. `other_org` shifts exist in
                // Phoebe under another org the user belongs to and are
                // recoverable with one click, so don't flag them as unsynced.
                const notFoundExternalShiftIds = nextResolvedItems
                    .filter((item) => item.status === 'not_found')
                    .map((item) => item.externalShiftId);

                if (notFoundExternalShiftIds.length > 0) {
                    // User-facing friction signal: the EHR shift(s) the user
                    // selected from the extension are not yet synced into
                    // Phoebe, so the UI renders a "Not synced to Phoebe yet"
                    // card and the bulk action skips them. Report to Sentry
                    // at `warning` level so we can track frequency per
                    // org/provider and prioritize sync coverage — this is
                    // not a crash, but it is something we want to know
                    // about.
                    Sentry.withScope((scope) => {
                        scope.setLevel('warning');
                        scope.setTag('provider', provider);
                        scope.setTag('organization_id', resolvedOrganizationId);
                        // Tag with the extension's manifest version when the
                        // shell forwarded it. Lets us attribute not-found
                        // reports to a specific build and spot regressions
                        // from out-of-date clients before they become a
                        // noisy baseline. Missing on old shells that predate
                        // the `EXTENSION_SIDEPANEL_CONFIG` contract.
                        if (extensionVersion !== null) {
                            scope.setTag('extension_version', extensionVersion);
                        }
                        scope.setContext('batch_resolve', {
                            not_found_count: notFoundExternalShiftIds.length,
                            total_count: externalShiftIds.length,
                            // Cap the sample at 10 IDs so the event payload
                            // stays bounded under large selections.
                            sample_external_shift_ids:
                                notFoundExternalShiftIds.slice(0, 10),
                        });
                        Sentry.captureMessage(
                            'Extension batch-resolve returned unsynced shifts',
                        );
                    });
                    logExtensionPageEvent(
                        'Batch resolve shifts returned unsynced shifts',
                        {
                            provider,
                            notFoundCount: notFoundExternalShiftIds.length,
                            totalCount: externalShiftIds.length,
                            resolvedOrganizationId,
                        },
                    );
                }

                setBatchResolvedItems(nextResolvedItems);
            } catch (error) {
                if (!cancelled) {
                    setBatchResolvedItems([]);
                    setBatchResolveError(
                        describeUnknownError(error) ??
                            'Phoebe could not look up the selected shifts right now.',
                    );
                    logExtensionPageEvent('Batch resolve shifts failed', {
                        error: describeUnknownError(error),
                    });
                }
            } finally {
                if (!cancelled) {
                    setIsBatchResolving(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        batchResolveRetryNonce,
        ehrSelectedShifts,
        effectiveUseMultiShiftCalloutResponse,
        extensionVersion,
        resolvedOrganizationId,
    ]);

    useEffect(() => {
        const userId = user?.id ?? null;
        if (pageShifts.length === 0 || !resolvedOrganizationId || !userId) {
            setPageShiftResolvedMap(new Map());
            return;
        }

        const provider = pageShifts[0]?.provider;
        if (!provider) {
            setPageShiftResolvedMap(new Map());
            return;
        }

        // Dedup page shifts by composite identity key so recurring
        // WellSky shifts (same externalShiftId, different date) each
        // get their own resolve entry.
        const seenIdentityKeys = new Set<string>();
        const externalShiftIds: Array<string> = [];
        const wellskyShiftUniqueIds: Array<string | null> = [];
        const identityKeys: Array<string> = [];
        for (const shift of pageShifts) {
            const key = buildShiftIdentityKey(shift);
            if (!seenIdentityKeys.has(key)) {
                seenIdentityKeys.add(key);
                externalShiftIds.push(shift.externalShiftId);
                wellskyShiftUniqueIds.push(shift.wellskyShiftUniqueId ?? null);
                identityKeys.push(key);
            }
        }
        const sourceUrl = pageShifts[0]?.sourceUrl ?? '';
        let cancelled = false;

        setPageShiftResolvedMap(new Map());

        void (async () => {
            try {
                const items = await resolvePageShiftBatchItems({
                    userId,
                    organizationId: resolvedOrganizationId,
                    provider,
                    sourceUrl,
                    externalShiftIds,
                    wellskyShiftUniqueIds,
                });
                if (cancelled) {
                    return;
                }
                // Items are index-paired with the input arrays, so map
                // each response item back to its composite identity key.
                const nextMap = new Map<string, BatchResolvedItem>();
                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    const identityKey = identityKeys[i];
                    const wellskyShiftUniqueId =
                        wellskyShiftUniqueIds[i] ?? null;
                    if (
                        item !== undefined &&
                        identityKey !== undefined &&
                        item.status === 'resolved' &&
                        item.patient_name !== null &&
                        item.patient_name !== undefined
                    ) {
                        nextMap.set(identityKey, {
                            externalShiftId: item.external_shift_id,
                            wellskyShiftUniqueId,
                            status: 'resolved',
                            phoebeShiftId: item.phoebe_shift_id ?? null,
                            patientName: item.patient_name,
                            shiftStartTime: item.shift_start_time ?? null,
                            shiftEndTime: item.shift_end_time ?? null,
                            shiftStatus: item.shift_status ?? null,
                            otherOrgMatches: [],
                        });
                    }
                }
                setPageShiftResolvedMap(nextMap);
            } catch {
                // Silently fall back to DOM-scraped titles on error.
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [pageShifts, resolvedOrganizationId, user?.id]);

    const shiftContextAnalytics = useMemo(() => {
        const resolvablePageInfo = getResolvablePageInfo(pageInfo);
        if (!resolvablePageInfo || resolvablePageInfo.entityType !== 'shifts') {
            return null;
        }

        return {
            provider: resolvablePageInfo.provider,
            externalId: resolvablePageInfo.externalId,
            sourceUrl: resolvablePageInfo.url,
        };
    }, [pageInfo]);
    const axisCareClientAnalytics = useMemo(() => {
        if (!isAxisCareClientPageInfo(pageInfo)) {
            return null;
        }

        return {
            externalId: pageInfo.externalId,
            sourceKind: pageInfo.url.includes('/admin/extension-lab/')
                ? 'extension_lab'
                : 'real',
            sourceUrl: pageInfo.url,
        };
    }, [pageInfo]);
    const twoWeeksFromNow = useMemo(() => {
        return new Date(
            Date.now() + CLIENT_SHIFT_LOOKAHEAD_DAYS * 24 * 60 * 60 * 1000,
        ).toISOString();
    }, []);

    const { data: contextualShift } = useShift(
        resolvedOrganizationId,
        resolvedShiftId,
        {
            reason: 'extension-shift-context',
        },
    );
    const {
        data: clientShiftSearch,
        isLoading: isLoadingClientShiftSearch,
        mutate: mutateClientShiftSearch,
    } = useShiftSearch(
        resolvedOrganizationId,
        null,
        {
            reason: 'extension-client-shifts',
        },
        {
            statuses: ['open', 'needs_coverage'],
            shift_end_date: twoWeeksFromNow,
            limit: CLIENT_SHIFT_SEARCH_LIMIT,
        },
        {
            enabled:
                axisCareClientAnalytics !== null && resolvedClientName !== null,
            revalidateOnFocus: false,
        },
    );
    const sidebarConversationSort = conversationPendingFirst
        ? 'pending_first'
        : undefined;
    const {
        conversations: sidebarConversations,
        isLoading: isLoadingSidebarConversations,
        isLoadingMore: isLoadingMoreSidebarConversations,
        isReachingEnd: isReachingEndSidebarConversations,
        size: sidebarConversationPageCount,
        setSize: setSidebarConversationPageCount,
        mutate: mutateSidebarConversations,
    } = useOrganizationConversationsRealtime({
        organizationId: resolvedOrganizationId,
        search: conversationSearch,
        sort: sidebarConversationSort,
    });
    const {
        conversations: contextualSidebarConversations,
        isLoading: isLoadingContextualSidebarConversations,
        mutate: mutateContextualSidebarConversations,
    } = useOrganizationConversationsRealtime({
        organizationId: resolvedOrganizationId,
        caregiverId: resolvedCaregiverId ?? undefined,
        sort: sidebarConversationSort,
    });
    const { data: sidebarUnreadCount, mutate: mutateSidebarUnreadCount } =
        useOrganizationUnreadCountRealtime(
            'extension-sidebar-conversations',
            true,
            resolvedOrganizationId,
        );
    const { data: sidebarPendingMessagesCount } =
        useOrganizationPendingMessagesCountRealtime(
            'extension-sidebar-conversations',
            {
                organizationId: resolvedOrganizationId,
            },
        );
    const mutateVisibleSidebarConversations = useCallback(async () => {
        await Promise.all([
            mutateSidebarConversations(),
            resolvedCaregiverId
                ? mutateContextualSidebarConversations()
                : Promise.resolve(),
        ]);
    }, [
        mutateContextualSidebarConversations,
        mutateSidebarConversations,
        resolvedCaregiverId,
    ]);
    const { markAsRead, markAsUnread } = useConversationActions(
        resolvedOrganizationId,
        mutateVisibleSidebarConversations,
        mutateSidebarUnreadCount,
    );
    const {
        data: contextualOutreaches,
        isLoading: isLoadingContextualOutreaches,
        mutate: mutateContextualOutreaches,
    } = useOutreachesBatch(
        resolvedOrganizationId,
        resolvedShiftId ? [resolvedShiftId] : [],
        {
            reason: 'extension-shift-context',
        },
        {
            outreachTypes: [...ACTIVE_SHIFT_OUTREACH_TYPES],
            statuses: ACTIVE_SHIFT_OUTREACH_STATUSES,
        },
    );
    const clientCandidateShifts = useMemo(() => {
        return filterShiftsForClient(
            clientShiftSearch?.shifts ?? [],
            resolvedClientId,
            resolvedClientName,
        );
    }, [clientShiftSearch?.shifts, resolvedClientId, resolvedClientName]);
    const clientCandidateShiftIds = useMemo(() => {
        return clientCandidateShifts.map((shift) => shift.id);
    }, [clientCandidateShifts]);
    const {
        data: clientCandidateOutreaches,
        isLoading: isLoadingClientCandidateOutreaches,
        mutate: mutateClientCandidateOutreaches,
    } = useOutreachesBatch(
        resolvedOrganizationId,
        clientCandidateShiftIds,
        {
            reason: 'extension-client-shifts',
        },
        {
            outreachTypes: [...ACTIVE_SHIFT_OUTREACH_TYPES],
            statuses: ACTIVE_SHIFT_OUTREACH_STATUSES,
        },
    );
    const clientEligibleShifts = useMemo(() => {
        const activeOutreachShiftIds = new Set<string>();

        for (const outreach of clientCandidateOutreaches ?? []) {
            for (const shiftId of outreach.shift_ids) {
                activeOutreachShiftIds.add(shiftId);
            }
        }

        return clientCandidateShifts.filter((shift) => {
            return (
                !activeOutreachShiftIds.has(shift.id) &&
                new Date(shift.shift_start_time).getTime() >= currentTimeMs
            );
        });
    }, [clientCandidateOutreaches, clientCandidateShifts, currentTimeMs]);
    const orderedClientEligibleShifts = useMemo(() => {
        return [...clientEligibleShifts].sort((left, right) => {
            return (
                new Date(left.shift_start_time).getTime() -
                new Date(right.shift_start_time).getTime()
            );
        });
    }, [clientEligibleShifts]);
    const clientEligibleShiftIds = useMemo(() => {
        return orderedClientEligibleShifts.map((shift) => shift.id);
    }, [orderedClientEligibleShifts]);
    const clientCreateOutreachUrl = useMemo(() => {
        if (clientEligibleShiftIds.length === 0) {
            return null;
        }

        return buildCreateOutreachUrl(
            clientEligibleShiftIds,
            resolvedOrganizationId,
        );
    }, [clientEligibleShiftIds, resolvedOrganizationId]);
    const contextualOutreachId = contextualOutreaches?.[0]?.id ?? null;
    const { data: contextualOutreach } = useJoinedOutreach(
        resolvedOrganizationId,
        contextualOutreachId,
        {
            reason: 'extension-shift-context',
        },
    );
    const contextualShiftFeedItem = useMemo(() => {
        if (!contextualOutreachId) {
            return null;
        }

        return (
            homeFeed?.outreaches.find(
                (outreach) => outreach.id === contextualOutreachId,
            ) ?? null
        );
    }, [contextualOutreachId, homeFeed?.outreaches]);
    const visibleHomeFeedOutreaches = useMemo(() => {
        const allOutreaches = homeFeed?.outreaches ?? [];
        if (!showOnlyMineFeedFilter || !user?.id) {
            return allOutreaches;
        }
        return allOutreaches.filter(
            (item) => item.created_by_user_id === user.id,
        );
    }, [homeFeed?.outreaches, showOnlyMineFeedFilter, user?.id]);
    const contextualClientFeedItems = useMemo(() => {
        if (!resolvedClientName) {
            return [];
        }

        return filterOutreachesForClient(
            visibleHomeFeedOutreaches,
            resolvedClientName,
        );
    }, [visibleHomeFeedOutreaches, resolvedClientName]);
    // Unfiltered variant for analytics: the PostHog
    // `EXTENSION_AXISCARE_CLIENT_DETECTED` event should report the true
    // number of matching outreaches for the client, not the count
    // skewed by the user's "Created by me" filter toggle. Using the
    // filtered `contextualClientFeedItems` here would systematically
    // underreport matches whenever a user leaves that filter enabled.
    const contextualClientFeedItemsForAnalytics = useMemo(() => {
        if (!resolvedClientName) {
            return [];
        }

        return filterOutreachesForClient(
            homeFeed?.outreaches ?? [],
            resolvedClientName,
        );
    }, [homeFeed?.outreaches, resolvedClientName]);

    const pinnedHomeFeedItems = contextualClientFeedItems;
    const pinnedContextLabel =
        contextualClientFeedItems.length > 0 ? 'For this client' : null;
    const remainingHomeFeedOutreaches = useMemo(() => {
        const pinnedOutreachIds = new Set([
            ...pinnedHomeFeedItems.map((item) => item.id),
            contextualShiftFeedItem?.id ?? null,
        ]);
        return visibleHomeFeedOutreaches.filter(
            (outreach) =>
                outreach.id !== contextualShiftFeedItem?.id &&
                !pinnedOutreachIds.has(outreach.id),
        );
    }, [
        contextualShiftFeedItem?.id,
        pinnedHomeFeedItems,
        visibleHomeFeedOutreaches,
    ]);
    const sortedPinnedHomeFeedItems = useMemo(() => {
        return sortExtensionOutreachFeedItems(pinnedHomeFeedItems, feedSort);
    }, [feedSort, pinnedHomeFeedItems]);
    const sortedRemainingHomeFeedOutreaches = useMemo(() => {
        return sortExtensionOutreachFeedItems(
            remainingHomeFeedOutreaches,
            feedSort,
        );
    }, [feedSort, remainingHomeFeedOutreaches]);
    const shouldShowFeedSortControl =
        sortedPinnedHomeFeedItems.length +
            sortedRemainingHomeFeedOutreaches.length >
        1;
    const isContextualShiftUnassigned =
        contextualShift?.assigned_caregiver_id === null ||
        contextualShift?.assigned_caregiver_id === undefined;
    const shouldShowShiftNoOutreachBanner =
        !!contextualShift &&
        !isLoadingContextualOutreaches &&
        (contextualOutreaches?.length ?? 0) === 0;
    const isContextualShiftCancelled =
        !!contextualShift && isCancelledShift(contextualShift);
    const shouldShowShiftQuickCalloutAction =
        shouldShowShiftNoOutreachBanner &&
        isContextualShiftUnassigned &&
        !isContextualShiftCancelled;
    const isContextualShiftPast =
        contextualShift !== undefined &&
        contextualShift !== null &&
        new Date(contextualShift.shift_end_time).getTime() <= currentTimeMs;
    const shouldShowClientQuickCalloutPrompt =
        axisCareClientAnalytics !== null &&
        resolvedClientName !== null &&
        !isLoadingClientShiftSearch &&
        !isLoadingClientCandidateOutreaches &&
        orderedClientEligibleShifts.length > 0;
    const shouldUseClientQuickCalloutFallback =
        orderedClientEligibleShifts.length >
        CLIENT_OUTREACH_SELECTION_THRESHOLD;
    const shouldShowSelectableClientShiftCards =
        shouldShowClientQuickCalloutPrompt &&
        !shouldUseClientQuickCalloutFallback;
    const selectedClientShiftIdSet = useMemo(() => {
        return new Set(selectedClientShiftIds);
    }, [selectedClientShiftIds]);
    const selectedClientShiftCount = selectedClientShiftIds.length;
    const allKnownSidebarConversations = useMemo(() => {
        const conversationsById = new Map<
            string,
            OrganizationConversationMetadata
        >();

        for (const conversation of contextualSidebarConversations) {
            conversationsById.set(conversation.id, conversation);
        }

        for (const conversation of sidebarConversations) {
            if (!conversationsById.has(conversation.id)) {
                conversationsById.set(conversation.id, conversation);
            }
        }

        return Array.from(conversationsById.values());
    }, [contextualSidebarConversations, sidebarConversations]);
    const knownSidebarConversationIds = useMemo(() => {
        return new Set(
            allKnownSidebarConversations.map((conversation) => conversation.id),
        );
    }, [allKnownSidebarConversations]);
    const selectedSidebarConversation = useMemo(() => {
        if (!selectedConversationId) {
            return undefined;
        }

        return allKnownSidebarConversations.find((conversation) => {
            return conversation.id === selectedConversationId;
        });
    }, [allKnownSidebarConversations, selectedConversationId]);
    const handleToggleBottomBar = useCallback((checked: boolean) => {
        if (!EXTENSION_BOTTOM_BAR_ENABLED) {
            return;
        }

        setShowBottomBar(checked);
        window.parent.postMessage(
            {
                type: 'EXTENSION_BOTTOM_BAR_TOGGLE',
                enabled: checked,
            },
            '*',
        );
    }, []);

    const handleToggleMinimizedRail = useCallback((checked: boolean) => {
        if (!EXTENSION_MINIMIZED_RAIL_ENABLED) {
            return;
        }

        setShowMinimizedRail(checked);
        setHasManualMinimizedRailPreference(true);
    }, []);
    const handleToggleSidebarOpenStateSync = useCallback((checked: boolean) => {
        setSyncSidebarOpenStateByDomain(checked);
    }, []);
    const selectConversationManually = useCallback((conversationId: string) => {
        isConversationWorkspacePinnedByUserRef.current = true;
        selectedConversationSourceRef.current = 'manual';
        setSelectedConversationId(conversationId);
    }, []);

    const isResolvingShiftContext =
        resolveContextRequest?.entity_type === 'shifts' &&
        (isLoadingResolvedContext ||
            (!!resolvedShiftId && isLoadingContextualOutreaches));
    const isResolvingClientContext =
        resolveContextRequest?.entity_type === 'clients' &&
        isLoadingResolvedContext;
    const [isSyncingClient, setIsSyncingClient] = useState(false);
    const shouldShowClientSyncAction =
        resolveContextRequest?.entity_type === 'clients' &&
        !isLoadingResolvedContext;
    const isUnresolvedClientContext =
        shouldShowClientSyncAction &&
        resolvedContext?.entity_type === 'clients' &&
        resolvedContext.status === 'not_found' &&
        resolvedContext.external_id === resolveContextRequest.external_id;
    const handleSyncClient = useCallback(async () => {
        if (
            !resolvedOrganizationId ||
            !resolveContextRequest ||
            resolveContextRequest.entity_type !== 'clients' ||
            !pageInfo?.provider
        ) {
            return;
        }
        setIsSyncingClient(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/extension/sync-client',
                pathParams: { organization_id: resolvedOrganizationId },
                body: {
                    provider: pageInfo.provider,
                    source_url: pageInfo.url,
                },
            });
            if (
                response.status === 'created' ||
                response.status === 'already_exists'
            ) {
                await mutateResolvedContext();
                toast.success('Client synced to Phoebe', {
                    persistNotification: false,
                });
            } else if (response.status === 'client_not_found') {
                toast.error('Client was not found in the EHR', {
                    persistNotification: false,
                });
            } else if (response.status === 'skipped_not_configured') {
                toast.error('Client sync is not configured for this EHR', {
                    persistNotification: false,
                });
            } else if (response.status === 'invalid_source_url') {
                toast.error('Could not identify this EHR client', {
                    persistNotification: false,
                });
            } else {
                toast.error('Could not sync this client', {
                    persistNotification: false,
                });
            }
        } catch {
            toast.error('Could not sync this client', {
                persistNotification: false,
            });
        } finally {
            setIsSyncingClient(false);
        }
    }, [
        mutateResolvedContext,
        pageInfo?.provider,
        pageInfo?.url,
        resolveContextRequest,
        resolvedOrganizationId,
    ]);
    const hasCaregiverContext =
        resolveContextRequest?.entity_type === 'caregivers';
    const contextualSidebarConversation = useMemo(() => {
        if (!hasCaregiverContext || !resolvedCaregiverId) {
            return null;
        }

        return contextualSidebarConversations[0] ?? null;
    }, [
        contextualSidebarConversations,
        hasCaregiverContext,
        resolvedCaregiverId,
    ]);
    const caregiverConversationContextKey = hasCaregiverContext
        ? `${resolvedCaregiverId ?? 'unresolved'}:${contextualSidebarConversation?.id ?? 'none'}`
        : null;
    const isLoadingAnySidebarConversations =
        isLoadingSidebarConversations ||
        (resolvedCaregiverId !== null &&
            isLoadingContextualSidebarConversations);

    // Shared throttle across every listener that refreshes the feed: the
    // home feed is the API's most expensive read for busy orgs, and callout
    // waves fire contact_attempt_updated many times per second. One
    // unthrottled sidebar turned an 8AM wave into ~6 feed requests/second
    // (2026-08-11 storm), starving the API event loop org-wide.
    // A throttle bounds how often a refetch starts, not how many overlap.
    const coalescedRefreshHomeFeed = useCoalescedCallback(() =>
        mutateHomeFeed(),
    );
    const refreshHomeFeed = useThrottledCallback(
        coalescedRefreshHomeFeed,
        REALTIME_REFRESH_DELAY_MS,
    );

    const throttledRefreshSidebarContext = useThrottledCallback(() => {
        if (resolvedShiftId) {
            void mutateContextualOutreaches();
        }
        if (resolvedClientName !== null) {
            void mutateClientShiftSearch();
            void mutateClientCandidateOutreaches();
        }
    }, REALTIME_REFRESH_DELAY_MS);

    const refreshSidebarData = useCallback(() => {
        refreshHomeFeed();
        setExtensionShiftRangeRefreshSignal(
            (currentSignal) => currentSignal + 1,
        );
        if (resolvedShiftId) {
            void mutateContextualOutreaches();
        }
        if (resolvedClientName !== null) {
            void mutateClientShiftSearch();
            void mutateClientCandidateOutreaches();
        }
    }, [
        mutateClientCandidateOutreaches,
        mutateClientShiftSearch,
        mutateContextualOutreaches,
        refreshHomeFeed,
        resolvedClientName,
        resolvedShiftId,
    ]);

    // Open an app-relative Phoebe path in a new tab via the background service
    // worker (EXTENSION_OPEN_PHOEBE_PATH → sidepanel relay → chrome.tabs.create
    // active:true) instead of window.open from this iframe. window.open from a
    // side-panel iframe opens the tab in the background intermittently, leaving
    // the user looking at the EHR tab; the service-worker path reliably focuses.
    const openPhoebePathInTab = useCallback((path: string) => {
        window.parent.postMessage(
            { type: 'EXTENSION_OPEN_PHOEBE_PATH', payload: { path } },
            '*',
        );
    }, []);

    // Voice input in the sidepanel iframe needs a one-time microphone grant
    // on the extension origin; the sidepanel opens the extension's grant
    // page in a normal tab where the getUserMedia prompt can render.
    const requestMicrophonePermission = useCallback(() => {
        window.parent.postMessage(
            { type: 'EXTENSION_MIC_PERMISSION_REQUEST' },
            '*',
        );
    }, []);

    const openOutreachInPhoebe = useCallback(
        (outreachId: string, source: string) => {
            posthog?.capture(EVENTS.EXTENSION_OPEN_IN_PHOEBE_CLICKED, {
                outreach_id: outreachId,
                source,
            });
            openPhoebePathInTab(
                buildOutreachDetailUrl(
                    outreachId,
                    resolvedOrganizationId,
                    'live',
                ),
            );
        },
        [openPhoebePathInTab, posthog, resolvedOrganizationId],
    );

    const openCreateOutreachInPhoebe = useCallback(
        (shiftIds: ReadonlyArray<string>, source: string) => {
            if (shiftIds.length === 0) {
                return;
            }

            posthog?.capture(EVENTS.EXTENSION_OPEN_IN_PHOEBE_CLICKED, {
                shift_count: shiftIds.length,
                source,
            });
            openPhoebePathInTab(
                buildCreateOutreachUrl(
                    shiftIds,
                    resolvedOrganizationId,
                    'live',
                ),
            );
        },
        [openPhoebePathInTab, posthog, resolvedOrganizationId],
    );
    const openConversationInPhoebe = useCallback(
        (conversationId: string) => {
            posthog?.capture(EVENTS.EXTENSION_OPEN_IN_PHOEBE_CLICKED, {
                conversation_id: conversationId,
                source: 'extension_sidebar_conversations',
            });
            openPhoebePathInTab(
                buildConversationDetailUrl(
                    conversationId,
                    resolvedOrganizationId,
                    'live',
                ),
            );
        },
        [openPhoebePathInTab, posthog, resolvedOrganizationId],
    );
    const loadMoreSidebarConversations = useCallback(() => {
        if (
            isLoadingMoreSidebarConversations ||
            isReachingEndSidebarConversations
        ) {
            return;
        }

        void setSidebarConversationPageCount(sidebarConversationPageCount + 1);
    }, [
        isLoadingMoreSidebarConversations,
        isReachingEndSidebarConversations,
        setSidebarConversationPageCount,
        sidebarConversationPageCount,
    ]);
    const handleMarkAllSidebarConversationsAsRead = useCallback(async () => {
        if (
            !resolvedOrganizationId ||
            isMarkingAllConversationsAsRead ||
            (sidebarUnreadCount ?? 0) === 0
        ) {
            return;
        }

        setIsMarkingAllConversationsAsRead(true);
        try {
            await getApiClient().post({
                url: '/organizations/{organization_id}/conversations/mark-all-read',
                pathParams: {
                    organization_id: resolvedOrganizationId,
                },
            });
            await mutateVisibleSidebarConversations();
            void mutateSidebarUnreadCount();
            toast.success('Marked all conversations as read', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } catch (error) {
            console.error(
                'Failed to mark sidebar conversations as read',
                error,
            );
            toast.error('Failed to mark all conversations as read', {
                persistNotification: true,
                notificationType: 'message_action',
            });
        } finally {
            setIsMarkingAllConversationsAsRead(false);
        }
    }, [
        isMarkingAllConversationsAsRead,
        mutateVisibleSidebarConversations,
        mutateSidebarUnreadCount,
        resolvedOrganizationId,
        sidebarUnreadCount,
    ]);
    const openSuggestedCalloutInPhoebe = useCallback(
        (outreachId: string) => {
            openOutreachInPhoebe(outreachId, 'feed_start_callout');
        },
        [openOutreachInPhoebe],
    );
    const contextualShiftPrimaryAction = useMemo(() => {
        if (contextualOutreachId) {
            // A cancelled shift keeps its outreach link but must not offer
            // assignment — staffing a dead shift is the worse outcome.
            if (
                !isContextualShiftCancelled &&
                canAssignFromOutreach(contextualOutreach ?? null)
            ) {
                return {
                    label: 'Assign with Phoebe',
                    variant: 'assign' as const,
                    title: 'Active outreach found',
                    description: 'Continue in Phoebe and choose a caregiver.',
                    onClick: () => {
                        openOutreachInPhoebe(
                            contextualOutreachId,
                            'shift_context_assign',
                        );
                    },
                };
            }

            return {
                label: 'View outreach',
                variant: 'open' as const,
                title: 'Active outreach found',
                description: 'Open this outreach in Phoebe.',
                onClick: () => {
                    openOutreachInPhoebe(
                        contextualOutreachId,
                        'shift_context_open',
                    );
                },
            };
        }

        if (contextualShift && isContextualShiftPast) {
            return null;
        }

        // Reached only when there is no active outreach to link to (that
        // branch returns above), so this suppresses just the "Find
        // caregivers" CTA that would deep-link a dead shift into the
        // composer.
        if (isContextualShiftCancelled) {
            return null;
        }

        if (contextualShift && isLoadingContextualOutreaches) {
            // The pinned page-shift snapshot can already know this shift maps
            // to an active outreach. Do not overwrite that authoritative
            // state with a speculative fill CTA while the contextual outreach
            // lookup is still loading.
            return null;
        }

        if (!contextualShift || !isContextualShiftUnassigned) {
            return null;
        }

        return {
            label: 'Find caregivers',
            variant: 'fill' as const,
            title: 'No active outreach for this shift',
            description: 'Start one in Phoebe.',
            onClick: () => {
                posthog?.capture(EVENTS.EXTENSION_CTA_FILL_CLICKED, {
                    cta_variant: 'fill',
                    source: 'web_app',
                    surface: 'extension_sidebar',
                    shift_id: contextualShift.id,
                    provider: pageInfo?.provider ?? null,
                });
                openCreateOutreachInPhoebe(
                    [contextualShift.id],
                    'shift_context_fill',
                );
            },
        };
    }, [
        contextualOutreach,
        contextualOutreachId,
        contextualShift,
        isLoadingContextualOutreaches,
        isContextualShiftCancelled,
        isContextualShiftPast,
        isContextualShiftUnassigned,
        openCreateOutreachInPhoebe,
        openOutreachInPhoebe,
        pageInfo,
        posthog,
    ]);
    const selectedShiftFeedPrimaryAction = useCallback(
        (item: HomeFeedItem) => {
            if (
                !contextualShiftFeedItem ||
                item.id !== contextualShiftFeedItem.id ||
                !contextualShiftPrimaryAction
            ) {
                return null;
            }

            return {
                label: contextualShiftPrimaryAction.label,
                onClick: contextualShiftPrimaryAction.onClick,
            };
        },
        [contextualShiftFeedItem, contextualShiftPrimaryAction],
    );
    const selectedShiftFeedCard = contextualShiftFeedItem ? (
        <OutreachFeedCompact
            getPrimaryAction={selectedShiftFeedPrimaryAction}
            homeFeedItems={[contextualShiftFeedItem]}
            isLoading={false}
            organizationId={resolvedOrganizationId}
        />
    ) : null;
    const publishedContextualShiftActionRef =
        useRef<FeedShiftActionUpdate | null>(null);
    const publishedFeedShiftActionsRef = useRef<
        Map<string, FeedShiftActionUpdate>
    >(new Map());

    useEffect(() => {
        if (
            !contextualShiftPrimaryAction ||
            !shiftContextAnalytics?.provider ||
            !shiftContextAnalytics.externalId
        ) {
            publishedContextualShiftActionRef.current = null;
            return;
        }

        const nextUpdate = {
            provider: shiftContextAnalytics.provider,
            externalShiftId: shiftContextAnalytics.externalId,
            label: contextualShiftPrimaryAction.label,
            outreachId: contextualOutreachId ?? undefined,
            sourceUrl: shiftContextAnalytics.sourceUrl,
            variant: contextualShiftPrimaryAction.variant,
        } satisfies FeedShiftActionUpdate;
        if (
            areFeedShiftActionUpdatesEqual(
                publishedContextualShiftActionRef.current,
                nextUpdate,
            )
        ) {
            return;
        }

        publishedContextualShiftActionRef.current = nextUpdate;
        postShiftActionUpdate(nextUpdate);
    }, [
        contextualOutreachId,
        contextualShiftPrimaryAction,
        shiftContextAnalytics,
    ]);

    useEffect(() => {
        const previousUpdates = publishedFeedShiftActionsRef.current;
        const nextUpdates = new Map(
            feedShiftActionUpdates.map((update) => [
                update.matchKey ?? update.externalShiftId ?? '',
                update,
            ]),
        );
        const allUpdateKeys = new Set([
            ...previousUpdates.keys(),
            ...nextUpdates.keys(),
        ]);

        for (const updateKey of allUpdateKeys) {
            const previousUpdate = previousUpdates.get(updateKey);
            const nextUpdate = nextUpdates.get(updateKey);

            if (
                nextUpdate &&
                !areFeedShiftActionUpdatesEqual(
                    previousUpdate ?? null,
                    nextUpdate,
                )
            ) {
                postShiftActionUpdate(nextUpdate);
                continue;
            }

            if (!nextUpdate && previousUpdate) {
                postShiftActionUpdate({
                    provider: previousUpdate.provider,
                    externalShiftId: previousUpdate.externalShiftId,
                    label: 'Find caregivers',
                    matchKey: previousUpdate.matchKey,
                    sourceUrl: previousUpdate.sourceUrl,
                    variant: 'fill',
                });
            }
        }

        publishedFeedShiftActionsRef.current = nextUpdates;
    }, [feedShiftActionUpdates]);

    useEffect(() => {
        const intervalId = window.setInterval(() => {
            setCurrentTimeMs(Date.now());
        }, CLIENT_SHIFT_TIME_REFRESH_MS);

        return () => {
            window.clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        const previousClientEligibleShiftIds =
            previousClientEligibleShiftIdsRef.current;

        setSelectedClientShiftIds((currentSelectedClientShiftIds) => {
            if (clientEligibleShiftIds.length === 0) {
                return [];
            }

            if (previousClientEligibleShiftIds.length === 0) {
                return clientEligibleShiftIds;
            }

            const previousClientEligibleShiftIdSet = new Set(
                previousClientEligibleShiftIds,
            );
            const currentSelectedClientShiftIdSet = new Set(
                currentSelectedClientShiftIds,
            );

            return clientEligibleShiftIds.filter((shiftId) => {
                if (currentSelectedClientShiftIdSet.has(shiftId)) {
                    return true;
                }

                return !previousClientEligibleShiftIdSet.has(shiftId);
            });
        });

        previousClientEligibleShiftIdsRef.current = clientEligibleShiftIds;
    }, [clientEligibleShiftIds]);

    useEffect(() => {
        const nextPageInfoKey = buildPageInfoKey(pageInfo);
        if (lastObservedPageInfoKeyRef.current === nextPageInfoKey) {
            return;
        }

        lastObservedPageInfoKeyRef.current = nextPageInfoKey;
        lastAutoFocusedPageShiftKeyRef.current = null;
    }, [pageInfo]);

    useEffect(() => {
        const hasVisiblePageShiftContext =
            pageShifts.length > 0 ||
            (effectiveUseMultiShiftCalloutResponse &&
                ehrSelectedShifts.length > 0);
        const nextPageShiftContextKey = hasVisiblePageShiftContext
            ? buildPageInfoKey(pageInfo)
            : null;
        if (nextPageShiftContextKey === null) {
            return;
        }

        if (
            activeTab === 'conversations' &&
            isConversationWorkspacePinnedByUserRef.current
        ) {
            lastAutoFocusedPageShiftKeyRef.current = nextPageShiftContextKey;
            return;
        }

        if (
            lastAutoFocusedPageShiftKeyRef.current === nextPageShiftContextKey
        ) {
            return;
        }

        lastAutoFocusedPageShiftKeyRef.current = nextPageShiftContextKey;
        setActiveTab('page_shifts');
    }, [
        activeTab,
        ehrSelectedShifts.length,
        effectiveUseMultiShiftCalloutResponse,
        pageInfo,
        pageShifts.length,
    ]);

    useEffect(() => {
        if (
            pageShifts.length > 0 ||
            (effectiveUseMultiShiftCalloutResponse &&
                ehrSelectedShifts.length > 0)
        ) {
            return;
        }

        // Selected-shift snapshots can arrive before the page-shift card feed,
        // especially when the sidepanel bootstraps from a direct
        // `sidepanel.html` tab. Treat either snapshot as valid "Shifts" tab
        // context so this restoration logic does not immediately yank the user
        // back to another tab and hide the selection they just made.
        if (hasCaregiverContext) {
            setActiveTab((currentActiveTab) => {
                if (currentActiveTab === 'page_shifts') {
                    return currentActiveTab;
                }

                if (currentActiveTab !== 'conversations') {
                    shouldRestoreDefaultTabRef.current = true;
                    return 'conversations';
                }

                return currentActiveTab;
            });
            return;
        }

        if (!shouldRestoreDefaultTabRef.current) {
            return;
        }

        shouldRestoreDefaultTabRef.current = false;
        setActiveTab((currentActiveTab) => {
            if (
                currentActiveTab === 'conversations' &&
                isConversationWorkspacePinnedByUserRef.current
            ) {
                return currentActiveTab;
            }

            return currentActiveTab === 'conversations'
                ? 'outreaches'
                : currentActiveTab;
        });
    }, [
        ehrSelectedShifts.length,
        effectiveUseMultiShiftCalloutResponse,
        hasCaregiverContext,
        pageShifts.length,
    ]);

    useEffect(() => {
        if (!hasCaregiverContext) {
            lastHandledCaregiverConversationContextKeyRef.current = null;
        }
    }, [hasCaregiverContext]);

    useEffect(() => {
        lastHandledCaregiverConversationContextKeyRef.current = null;
        shouldRestoreDefaultTabRef.current = false;
        isConversationWorkspacePinnedByUserRef.current = false;
        selectedConversationSourceRef.current = null;
        setSelectedConversationId(null);
    }, [resolvedOrganizationId]);

    useEffect(() => {
        if (
            !selectedConversationId ||
            selectedConversationSourceRef.current !== 'auto_context' ||
            isLoadingAnySidebarConversations ||
            knownSidebarConversationIds.has(selectedConversationId)
        ) {
            return;
        }

        selectedConversationSourceRef.current = null;
        setSelectedConversationId(null);
    }, [
        isLoadingAnySidebarConversations,
        knownSidebarConversationIds,
        selectedConversationId,
    ]);

    useEffect(() => {
        if (!hasCaregiverContext || isLoadingAnySidebarConversations) {
            return;
        }

        if (
            caregiverConversationContextKey ===
            lastHandledCaregiverConversationContextKeyRef.current
        ) {
            return;
        }

        if (
            selectedConversationId !== null &&
            selectedConversationSourceRef.current === 'manual'
        ) {
            return;
        }

        lastHandledCaregiverConversationContextKeyRef.current =
            caregiverConversationContextKey;
        if (contextualSidebarConversation === null) {
            if (
                selectedConversationId !== null &&
                selectedConversationSourceRef.current === 'auto_context'
            ) {
                selectedConversationSourceRef.current = null;
                setSelectedConversationId(null);
            }
            return;
        }

        if (selectedConversationId !== contextualSidebarConversation.id) {
            selectedConversationSourceRef.current = 'auto_context';
            setSelectedConversationId(contextualSidebarConversation.id);
        }
    }, [
        caregiverConversationContextKey,
        contextualSidebarConversation,
        hasCaregiverContext,
        isLoadingAnySidebarConversations,
        selectedConversationId,
    ]);

    useListenForOrganizationEvent(
        'contact_attempt_updated',
        () => {
            refreshHomeFeed();
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'shift_confirmation_updated',
        () => {
            refreshSidebarData();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'shifts_updated',
        () => {
            refreshSidebarData();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_added',
        () => {
            refreshHomeFeed();
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'outreach_suggestion_removed',
        () => {
            refreshHomeFeed();
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'agent_conversation_updated',
        () => {
            refreshHomeFeed();
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'agent_conversation_stream_event',
        () => {
            // Stream chunks fire per token, so they never refresh the home
            // feed; the feed waits for agent_conversation_updated on turn
            // completion.
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );
    useListenForOrganizationEvent(
        'agent_run_stream_event',
        () => {
            // Stream chunks fire per token, so they never refresh the home
            // feed; the feed waits for agent_conversation_updated on turn
            // completion.
            throttledRefreshSidebarContext();
        },
        { organizationId: resolvedOrganizationId },
    );

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            if (!isAllowedOrigin(event.origin)) {
                return;
            }
            if (event.data?.type === 'PAGE_INFO_UPDATE') {
                const nextPageInfo = isPageInfoPayload(event.data.payload)
                    ? event.data.payload
                    : null;
                if (nextPageInfo === null) {
                    logExtensionPageEvent(
                        'Ignored PAGE_INFO_UPDATE with invalid payload',
                        {
                            origin: event.origin,
                            payloadType: typeof event.data?.payload,
                        },
                    );
                    return;
                }
                logExtensionPageEvent('Received PAGE_INFO_UPDATE', {
                    provider: nextPageInfo.provider,
                    entityType: nextPageInfo.entityType ?? null,
                    externalId: nextPageInfo.externalId ?? null,
                    url: nextPageInfo.url,
                    origin: event.origin,
                });
                setPageInfo((previousPageInfo) => {
                    return buildPageInfoKey(previousPageInfo) ===
                        buildPageInfoKey(nextPageInfo)
                        ? previousPageInfo
                        : nextPageInfo;
                });
                return;
            }
            if (event.data?.type === 'EXTENSION_TRACK_EVENT') {
                const { event: eventName, properties } = event.data.payload as {
                    readonly event: string;
                    readonly properties?: Record<string, unknown>;
                };
                const mergedProperties = {
                    source: 'chrome_extension',
                    ...(properties ?? {}),
                };
                if (eventName === 'extension_cta_shown') {
                    logExtensionEvent(eventName, mergedProperties);
                } else {
                    posthog?.capture(eventName, mergedProperties);
                }
                return;
            }
            if (event.data?.type === 'EXTENSION_SELECTED_SHIFTS_UPDATE') {
                const payload = event.data.payload as {
                    readonly shifts?: ReadonlyArray<SelectedExtensionShift>;
                } | null;
                const nextSelectedShifts = payload?.shifts
                    ? [...payload.shifts]
                    : [];
                setEhrSelectedShifts((currentSelectedShifts) => {
                    // These messages are rebroadcast snapshots from the
                    // background source of truth. Keep this setter idempotent
                    // so identical echoes are real no-ops instead of another
                    // rerender step in a feedback loop.
                    return areSelectedExtensionShiftsEqual(
                        currentSelectedShifts,
                        nextSelectedShifts,
                    )
                        ? currentSelectedShifts
                        : nextSelectedShifts;
                });
                return;
            }
            if (event.data?.type === 'EXTENSION_SIDEPANEL_CONFIG') {
                const nextExtensionId = getSidepanelExtensionId(
                    event.data.payload,
                );
                const nextExtensionInstallType =
                    getSidepanelExtensionInstallType(event.data.payload);
                const nextExtensionVersion = getSidepanelExtensionVersion(
                    event.data.payload,
                );
                const nextSyncSidebarOpenStateByDomain =
                    getSidepanelSyncSidebarOpenStateByDomain(
                        event.data.payload,
                    );
                setSidepanelConfig((currentConfig) => {
                    if (
                        (currentConfig?.extensionId ?? null) ===
                            nextExtensionId &&
                        (currentConfig?.extensionInstallType ?? null) ===
                            nextExtensionInstallType &&
                        currentConfig?.syncSidebarOpenStateByDomain ===
                            nextSyncSidebarOpenStateByDomain &&
                        (currentConfig?.extensionVersion ?? null) ===
                            nextExtensionVersion
                    ) {
                        return currentConfig;
                    }

                    if (
                        nextExtensionId === null &&
                        nextExtensionInstallType === null &&
                        nextSyncSidebarOpenStateByDomain === null &&
                        nextExtensionVersion === null
                    ) {
                        return null;
                    }

                    return {
                        ...(nextExtensionId !== null && {
                            extensionId: nextExtensionId,
                        }),
                        ...(nextExtensionInstallType !== null && {
                            extensionInstallType: nextExtensionInstallType,
                        }),
                        ...(nextSyncSidebarOpenStateByDomain !== null && {
                            syncSidebarOpenStateByDomain:
                                nextSyncSidebarOpenStateByDomain,
                        }),
                        ...(nextExtensionVersion !== null && {
                            extensionVersion: nextExtensionVersion,
                        }),
                    };
                });
                return;
            }
            if (event.data?.type === 'EXTENSION_PAGE_SHIFTS_UPDATE') {
                const payload = event.data.payload as {
                    readonly shifts?: ReadonlyArray<unknown>;
                } | null;
                const nextPageShifts = (payload?.shifts ?? []).flatMap(
                    (shift) => {
                        return isExtensionPageShiftPayload(shift)
                            ? [shift]
                            : [];
                    },
                );
                setPageShifts((currentPageShifts) => {
                    // Same rule as selected shifts: applying a logically
                    // identical page snapshot must not trigger more sidebar
                    // work, or render bursts can feed themselves.
                    return areExtensionPageShiftsEqual(
                        currentPageShifts,
                        nextPageShifts,
                    )
                        ? currentPageShifts
                        : nextPageShifts;
                });
                return;
            }
        }

        window.addEventListener('message', handleMessage);
        logExtensionPageEvent('Posting EXTENSION_PAGE_READY');
        window.parent.postMessage({ type: 'EXTENSION_PAGE_READY' }, '*');
        window.parent.postMessage(
            { type: 'EXTENSION_GET_SELECTED_SHIFTS' },
            '*',
        );
        window.parent.postMessage({ type: 'EXTENSION_GET_PAGE_SHIFTS' }, '*');

        return () => window.removeEventListener('message', handleMessage);
    }, [effectiveUseMultiShiftCalloutResponse, posthog]);

    useEffect(() => {
        if (pageInfo !== null) {
            return;
        }

        const retryIntervalId = window.setInterval(() => {
            logExtensionPageEvent(
                'Retrying EXTENSION_PAGE_READY while waiting for page info',
            );
            window.parent.postMessage({ type: 'EXTENSION_PAGE_READY' }, '*');
        }, 700);

        return () => {
            window.clearInterval(retryIntervalId);
        };
    }, [pageInfo]);

    useEffect(() => {
        if (hasTrackedSidebarOpenRef.current || !posthog) {
            return;
        }

        hasTrackedSidebarOpenRef.current = true;
        posthog?.capture(EVENTS.EXTENSION_SIDEBAR_OPENED);
    }, [posthog]);

    useEffect(() => {
        if (!resolvedOrganizationId || !user) {
            return;
        }

        const runtimeConfig = getRuntimeConfig();
        const resolvedPhoebeAppUrl = window.location.origin;
        logExtensionPageEvent('Posting EXTENSION_CONFIG', {
            organizationId: resolvedOrganizationId,
            organizationName: resolvedOrganizationName,
            userId: user.id,
            allowedDomainCount: extensionConfig?.allowed_domains.length ?? 0,
        });
        window.parent.postMessage(
            {
                type: 'EXTENSION_CONFIG',
                payload: {
                    phoebeAppUrl: resolvedPhoebeAppUrl,
                    kratosPublicUrl: new URL(
                        runtimeConfig.kratosPublicUrl,
                        resolvedPhoebeAppUrl,
                    ).toString(),
                    organizationId: resolvedOrganizationId,
                    organizationName: resolvedOrganizationName,
                    userId: user.id,
                    userName: getUserName(user),
                    apiUrl: new URL(
                        runtimeConfig.apiUrl,
                        resolvedPhoebeAppUrl,
                    ).toString(),
                    allowedDomains: extensionConfig?.allowed_domains ?? [],
                },
            },
            '*',
        );
    }, [
        extensionConfig,
        resolvedOrganizationName,
        resolvedOrganizationId,
        user,
    ]);

    useEffect(() => {
        if (!EXTENSION_MINIMIZED_RAIL_ENABLED) {
            return;
        }

        if (
            extensionPrefUserId === null ||
            hydratedMinimizedRailPrefUserId !== extensionPrefUserId
        ) {
            return;
        }

        const storageKey = `${MINIMIZED_RAIL_STORAGE_KEY}:${extensionPrefUserId}`;
        const manualStorageKey = `${MINIMIZED_RAIL_MANUAL_STORAGE_KEY}:${extensionPrefUserId}`;
        try {
            if (hasManualMinimizedRailPreference) {
                localStorage.setItem(storageKey, String(showMinimizedRail));
                localStorage.setItem(manualStorageKey, 'true');
            } else {
                localStorage.removeItem(storageKey);
                localStorage.removeItem(manualStorageKey);
            }
        } catch {
            // localStorage may be unavailable
        }

        window.parent.postMessage(
            {
                type: 'EXTENSION_MINIMIZED_RAIL_TOGGLE',
                enabled: showMinimizedRail,
                manuallyConfigured: hasManualMinimizedRailPreference,
            },
            '*',
        );
    }, [
        extensionPrefUserId,
        hasManualMinimizedRailPreference,
        hydratedMinimizedRailPrefUserId,
        showMinimizedRail,
    ]);

    useEffect(() => {
        if (!hasHydratedSidebarOpenStateSync) {
            return;
        }

        window.parent.postMessage(
            {
                type: 'EXTENSION_SYNC_SIDEBAR_OPEN_STATE_BY_DOMAIN_TOGGLE',
                enabled: syncSidebarOpenStateByDomain,
            },
            '*',
        );
    }, [hasHydratedSidebarOpenStateSync, syncSidebarOpenStateByDomain]);

    useEffect(() => {
        if (!EXTENSION_BOTTOM_BAR_ENABLED) {
            return;
        }

        const count = activeBottomBarOutreaches.length;
        const actionableCount = activeBottomBarOutreaches.filter(
            (item) => item.needs_action,
        ).length;
        const nextCountKey = `${count}:${actionableCount}`;
        if (lastPostedOutreachCountKeyRef.current === nextCountKey) {
            return;
        }

        lastPostedOutreachCountKeyRef.current = nextCountKey;
        logExtensionPageEvent('Posting EXTENSION_OUTREACH_COUNT', {
            count,
            actionableCount,
        });
        window.parent.postMessage(
            {
                type: 'EXTENSION_OUTREACH_COUNT',
                count,
                actionableCount,
            },
            '*',
        );
    }, [activeBottomBarOutreaches]);

    useEffect(() => {
        if (!hasTrackedFeedViewedRef.current && homeFeed && posthog) {
            hasTrackedFeedViewedRef.current = true;
            posthog?.capture(EVENTS.EXTENSION_SIDEBAR_FEED_VIEWED, {
                outreach_count: homeFeed.outreaches.length,
                provider: pageInfo?.provider ?? null,
                entity_type: pageInfo?.entityType ?? null,
            });
        }
    }, [homeFeed, pageInfo, posthog]);

    useEffect(() => {
        if (!shiftContextAnalytics || !resolvedShiftId) {
            if (!shiftContextAnalytics) {
                lastTrackedShiftContextRef.current = null;
            }
            return;
        }

        const contextKey = `${shiftContextAnalytics.provider}:${shiftContextAnalytics.externalId}:${resolvedShiftId}`;
        if (lastTrackedShiftContextRef.current === contextKey) {
            return;
        }

        lastTrackedShiftContextRef.current = contextKey;
        logExtensionEvent('extension_shift_context_detected', {
            external_id: shiftContextAnalytics.externalId,
            provider: shiftContextAnalytics.provider,
            shift_id: resolvedShiftId,
            source_url: shiftContextAnalytics.sourceUrl,
        });
    }, [resolvedShiftId, shiftContextAnalytics]);

    useEffect(() => {
        if (!shiftContextAnalytics || !resolvedShiftId) {
            if (!shiftContextAnalytics) {
                lastTrackedShiftContextOutreachRef.current = null;
                lastTrackedShiftContextNoOutreachRef.current = null;
            }
            return;
        }

        if (isLoadingContextualOutreaches) {
            return;
        }

        const contextKey = `${shiftContextAnalytics.provider}:${shiftContextAnalytics.externalId}:${resolvedShiftId}`;
        if (contextualOutreachId) {
            if (lastTrackedShiftContextOutreachRef.current === contextKey) {
                return;
            }

            lastTrackedShiftContextOutreachRef.current = contextKey;
            logExtensionEvent('extension_shift_context_outreach_found', {
                external_id: shiftContextAnalytics.externalId,
                outreach_id: contextualOutreachId,
                provider: shiftContextAnalytics.provider,
                shift_id: resolvedShiftId,
                source_url: shiftContextAnalytics.sourceUrl,
            });
            return;
        }

        if ((contextualOutreaches?.length ?? 0) !== 0) {
            return;
        }

        if (lastTrackedShiftContextNoOutreachRef.current === contextKey) {
            return;
        }

        lastTrackedShiftContextNoOutreachRef.current = contextKey;
        logExtensionEvent('extension_shift_context_no_outreach', {
            external_id: shiftContextAnalytics.externalId,
            provider: shiftContextAnalytics.provider,
            shift_id: resolvedShiftId,
            source_url: shiftContextAnalytics.sourceUrl,
        });
    }, [
        contextualOutreachId,
        contextualOutreaches,
        isLoadingContextualOutreaches,
        resolvedShiftId,
        shiftContextAnalytics,
    ]);

    useEffect(() => {
        if (!axisCareClientAnalytics || !resolvedOrganizationId) {
            if (!axisCareClientAnalytics) {
                lastTrackedAxisCareClientRef.current = null;
            }
            return;
        }

        if (isLoadingResolvedContext) {
            return;
        }

        const contextKey = `${axisCareClientAnalytics.externalId}:${resolvedClientName ?? 'not_found'}:${axisCareClientAnalytics.sourceKind}`;
        if (lastTrackedAxisCareClientRef.current === contextKey) {
            return;
        }

        lastTrackedAxisCareClientRef.current = contextKey;
        logExtensionEvent('extension_axiscare_client_detected', {
            client_name: resolvedClientName,
            external_id: axisCareClientAnalytics.externalId,
            matched_outreach_count:
                contextualClientFeedItemsForAnalytics.length,
            resolved_client: resolvedClientName !== null,
            source_kind: axisCareClientAnalytics.sourceKind,
            source_url: axisCareClientAnalytics.sourceUrl,
        });
    }, [
        axisCareClientAnalytics,
        contextualClientFeedItemsForAnalytics.length,
        isLoadingResolvedContext,
        resolvedClientName,
        resolvedOrganizationId,
    ]);

    const visibleEhrSelectedShifts = useMemo(() => {
        // The EHR page/background own the authoritative selected-shift state.
        // This app surface may resolve organization features later or against a
        // different org while bootstrapping. Hide unsupported selection UI here,
        // but do not clear the background selection from this non-authoritative
        // view or we can erase a real page selection as the iframe mounts.
        //
        // While the org-features decision is still unresolved
        // (`isEffectiveUseMultiShiftCalloutResponseUnresolved`) render
        // optimistically. Previously we checked
        // `effectiveUseMultiShiftCalloutResponse` truthiness, but
        // `useFeatureFlag` defaults to `false` during the loading
        // window, so the panel was hidden for the duration of the
        // org-features round trip — making the "Selected shifts"
        // section feel laggy right after a checkbox click even though
        // the selection data was already in state.
        if (isEffectiveUseMultiShiftCalloutResponseUnresolved) {
            return ehrSelectedShifts;
        }
        return effectiveUseMultiShiftCalloutResponse ? ehrSelectedShifts : [];
    }, [
        ehrSelectedShifts,
        effectiveUseMultiShiftCalloutResponse,
        isEffectiveUseMultiShiftCalloutResponseUnresolved,
    ]);
    const isSelectingEhrShifts = visibleEhrSelectedShifts.length > 0;
    const selectedEhrShiftIdentityKeySet = useMemo(() => {
        return new Set(
            visibleEhrSelectedShifts.map((shift) =>
                buildShiftIdentityKey(shift),
            ),
        );
    }, [visibleEhrSelectedShifts]);
    const lastResolvedPinnedPageShiftRef = useRef<ExtensionPageShift | null>(
        null,
    );
    const pinnedPageShift = useMemo(() => {
        if (
            pageInfo?.entityType !== 'shifts' ||
            pageInfo.externalId === undefined
        ) {
            return null;
        }

        return (
            pageShifts.find(
                (shift) => shift.externalShiftId === pageInfo.externalId,
            ) ?? null
        );
    }, [pageInfo?.entityType, pageInfo?.externalId, pageShifts]);
    const currentPageShift = useMemo(() => {
        if (pinnedPageShift === null) {
            return null;
        }

        const lastResolvedPinnedPageShift =
            lastResolvedPinnedPageShiftRef.current;
        if (
            pageInfo?.entityType !== 'shifts' ||
            pageInfo.externalId !== pinnedPageShift.externalShiftId
        ) {
            return pinnedPageShift;
        }

        if (contextualShiftPrimaryAction) {
            // The iframe can resolve the pinned shift into a real Phoebe
            // outreach state even when the EHR page is still showing a stale
            // scraped CTA snapshot. Prefer that contextual action for the
            // pinned "Current shift" card so the sidebar reflects the
            // authoritative app state instead of lagging the page-level
            // scrape.
            return {
                ...pinnedPageShift,
                activeOutreachId:
                    contextualShiftPrimaryAction.variant === 'fill'
                        ? null
                        : contextualOutreachId,
                label: contextualShiftPrimaryAction.label,
                variant: contextualShiftPrimaryAction.variant,
            } satisfies ExtensionPageShift;
        }

        if (
            lastResolvedPinnedPageShift?.externalShiftId ===
                pinnedPageShift.externalShiftId &&
            lastResolvedPinnedPageShift.activeOutreachId !== null
        ) {
            // Do not snap the selected shift back to a stale "Fill with
            // Phoebe" scrape if context resolution or the pinned feed card
            // briefly disappear during a refresh. Holding on to the last
            // resolved active-outreach action prevents transient regressions in
            // the sidebar without introducing another publish loop.
            return {
                ...pinnedPageShift,
                activeOutreachId: lastResolvedPinnedPageShift.activeOutreachId,
                label: lastResolvedPinnedPageShift.label,
                variant: lastResolvedPinnedPageShift.variant,
            } satisfies ExtensionPageShift;
        }

        return pinnedPageShift;
    }, [
        contextualOutreachId,
        contextualShiftPrimaryAction,
        pageInfo?.entityType,
        pageInfo?.externalId,
        pinnedPageShift,
    ]);
    useEffect(() => {
        if (
            currentPageShift?.activeOutreachId !== null &&
            pageInfo?.entityType === 'shifts' &&
            pageInfo.externalId === currentPageShift?.externalShiftId
        ) {
            lastResolvedPinnedPageShiftRef.current = currentPageShift;
            return;
        }

        if (
            pageInfo?.entityType !== 'shifts' ||
            currentPageShift === null ||
            pageInfo.externalId !== currentPageShift.externalShiftId
        ) {
            lastResolvedPinnedPageShiftRef.current = null;
        }
    }, [currentPageShift, pageInfo?.entityType, pageInfo?.externalId]);
    const outreachTabShiftPrimaryAction = useMemo(() => {
        if (contextualShiftPrimaryAction) {
            return contextualShiftPrimaryAction;
        }

        // A cancelled shift has no contextual action by design. The scraped
        // page snapshot below predates that decision — and on any extension
        // build older than the one suppressing these CTAs it still reads
        // "Find caregivers" — so falling through would rebuild exactly the
        // deep link this change exists to remove.
        if (isContextualShiftCancelled) {
            return null;
        }

        if (currentPageShift === null) {
            return null;
        }

        const description =
            currentPageShift.variant === 'assign'
                ? 'Continue in Phoebe and choose a caregiver.'
                : currentPageShift.variant === 'open'
                  ? 'Open this outreach in Phoebe.'
                  : 'Start one in Phoebe.';
        const title =
            currentPageShift.variant === 'fill'
                ? 'No active outreach for this shift'
                : 'Active outreach found';

        // The Shifts tab can already recover the correct CTA from the scraped
        // page-shift snapshot even when full shift-context resolution lags
        // behind. Reuse that same action as an Outreaches-tab fallback so the
        // selected shift never loses its primary Phoebe action just because
        // the richer contextual card has not materialized yet.
        return {
            label: currentPageShift.label,
            onClick: () => {
                handleOpenPageShiftInPhoebe(currentPageShift);
            },
            title,
            description,
        };
    }, [
        contextualShiftPrimaryAction,
        currentPageShift,
        isContextualShiftCancelled,
    ]);
    const pageShiftsExcludingSelected = useMemo(() => {
        return pageShifts.filter(
            (shift) =>
                !selectedEhrShiftIdentityKeySet.has(
                    buildShiftIdentityKey(shift),
                ),
        );
    }, [pageShifts, selectedEhrShiftIdentityKeySet]);
    const visibleCurrentPageShift = useMemo(() => {
        if (isSelectingEhrShifts) {
            return null;
        }

        return currentPageShift;
    }, [currentPageShift, isSelectingEhrShifts]);
    const visibleRemainingPageShifts = useMemo(() => {
        if (visibleCurrentPageShift === null) {
            return pageShiftsExcludingSelected;
        }

        const currentKey = buildShiftIdentityKey(visibleCurrentPageShift);
        return pageShiftsExcludingSelected.filter(
            (shift) => buildShiftIdentityKey(shift) !== currentKey,
        );
    }, [pageShiftsExcludingSelected, visibleCurrentPageShift]);
    const isInitializing =
        isLoadingUser ||
        isLoadingOrganizationContext ||
        (!resolvedOrganizationId && memberships.length === 0);
    const batchResolvedItemsByIdentityKey = useMemo(() => {
        return new Map(
            batchResolvedItems.map((item) => [
                buildShiftIdentityKey(item),
                item,
            ]),
        );
    }, [batchResolvedItems]);
    const resolvedSelectedShiftCount = useMemo(() => {
        return batchResolvedItems.filter(
            (item) => item.status === 'resolved' && item.phoebeShiftId !== null,
        ).length;
    }, [batchResolvedItems]);
    const hasCancelledSelectedShift = useMemo(
        () =>
            batchResolvedItems.some(
                (item) =>
                    item.status === 'resolved' &&
                    item.shiftStatus === 'cancelled',
            ),
        [batchResolvedItems],
    );
    // When every cross-org shift in the selection has exactly one
    // candidate org in common, surface a single "Switch to {org}"
    // affordance at the top of the list and skip the per-row buttons.
    // The same EHR shift can match multiple Phoebe orgs the user
    // belongs to (franchise locations sharing one EHR account, etc.),
    // so we compute the intersection of each shift's match-set: only
    // banner if exactly one org appears in every set. Otherwise the
    // per-row chooser handles the ambiguous case without forcing a
    // guess.
    const commonOtherOrgSuggestion = useMemo<{
        readonly organizationId: string;
        readonly organizationName: string;
        readonly count: number;
    } | null>(() => {
        const otherOrgItems = batchResolvedItems.filter(
            (item) =>
                item.status === 'other_org' && item.otherOrgMatches.length > 0,
        );
        if (otherOrgItems.length === 0) {
            return null;
        }
        const sharedOrgIds = otherOrgItems.reduce<Set<string>>(
            (intersection, item, index) => {
                const itemOrgIds = new Set(
                    item.otherOrgMatches.map((match) => match.organizationId),
                );
                if (index === 0) {
                    return itemOrgIds;
                }
                return new Set(
                    Array.from(intersection).filter((id) => itemOrgIds.has(id)),
                );
            },
            new Set<string>(),
        );
        if (sharedOrgIds.size !== 1) {
            return null;
        }
        const sharedOrgId = Array.from(sharedOrgIds)[0];
        if (sharedOrgId === undefined) {
            return null;
        }
        const sharedMatch = otherOrgItems[0]?.otherOrgMatches.find(
            (match) => match.organizationId === sharedOrgId,
        );
        return {
            organizationId: sharedOrgId,
            organizationName:
                sharedMatch?.organizationName ?? 'another organization',
            count: otherOrgItems.length,
        };
    }, [batchResolvedItems]);
    // Multi-org analogue of `commonOtherOrgSuggestion`: when 2+ selected
    // shifts all live in 2+ shared candidate orgs, surface the full list
    // as clickable chips under the selection. Only fires for 2+ selected
    // shifts so it doesn't duplicate the per-row chooser in the
    // single-shift case, and only for intersection size >= 2 so it never
    // acts as a filter -- every listed org has snapshots of every
    // selected shift, matching the "also in: Org A, Org B" UX for
    // franchises that share an EHR.
    const commonOtherOrgMatches = useMemo<
        ReadonlyArray<{
            readonly organizationId: string;
            readonly organizationName: string;
        }>
    >(() => {
        const otherOrgItems = batchResolvedItems.filter(
            (item) =>
                item.status === 'other_org' && item.otherOrgMatches.length > 0,
        );
        if (otherOrgItems.length < 2) {
            return [];
        }
        const sharedOrgIds = otherOrgItems.reduce<Set<string>>(
            (intersection, item, index) => {
                const itemOrgIds = new Set(
                    item.otherOrgMatches.map((match) => match.organizationId),
                );
                if (index === 0) {
                    return itemOrgIds;
                }
                return new Set(
                    Array.from(intersection).filter((id) => itemOrgIds.has(id)),
                );
            },
            new Set<string>(),
        );
        if (sharedOrgIds.size < 2) {
            return [];
        }
        const namesByOrgId = new Map<string, string>();
        for (const item of otherOrgItems) {
            for (const match of item.otherOrgMatches) {
                if (
                    sharedOrgIds.has(match.organizationId) &&
                    !namesByOrgId.has(match.organizationId)
                ) {
                    namesByOrgId.set(
                        match.organizationId,
                        match.organizationName ?? match.organizationId,
                    );
                }
            }
        }
        return Array.from(sharedOrgIds).map((organizationId) => ({
            organizationId,
            organizationName:
                namesByOrgId.get(organizationId) ?? organizationId,
        }));
    }, [batchResolvedItems]);
    // Only show active resolution UI while the request can actually make
    // progress. Otherwise boot-time org loading or a failed API request looks
    // like a live spinner forever, which hides the real blocked state.
    const selectedShiftResolutionState: SelectedShiftResolutionState =
        visibleEhrSelectedShifts.length === 0
            ? 'idle'
            : isInitializing && !resolvedOrganizationId
              ? 'waiting_for_context'
              : isBatchResolving
                ? 'resolving'
                : batchResolveError !== null
                  ? 'error'
                  : 'ready';
    // Surface the EHR-side title/subtitle (e.g. "Test Client" + "04/17/2026 ·
    // 9a - 10:45a") for unresolved shifts so the list stays readable even
    // when Phoebe has not yet synced the shift record. The raw external ID
    // is not meaningful to coordinators.
    const pageShiftByIdentityKey = useMemo(() => {
        return new Map(
            pageShifts.map((shift) => [buildShiftIdentityKey(shift), shift]),
        );
    }, [pageShifts]);

    if (!isInitializing && (!user || !resolvedOrganizationId)) {
        return (
            <div className="bg-muted/20 min-h-screen p-4">
                <div className={cn(EXTENSION_CARD_CLASS, 'space-y-2 p-4')}>
                    <h1 className="text-foreground text-sm font-semibold">
                        Could not initialize the sidebar
                    </h1>
                    <p className="text-muted-foreground text-sm">
                        Phoebe could not determine your signed-in user or active
                        organization.
                    </p>
                </div>
            </div>
        );
    }

    function handleCreateQuickOutreach() {
        if (!resolvedOrganizationId || !contextualShift) {
            return;
        }

        posthog?.capture(EVENTS.EXTENSION_QUICK_OUTREACH_CREATED, {
            shift_id: contextualShift.id,
            provider: pageInfo?.provider ?? null,
            external_id: getResolvablePageInfo(pageInfo)?.externalId ?? null,
        });
        openCreateOutreachInPhoebe(
            [contextualShift.id],
            'shift_create_outreach',
        );
    }

    function handleCreateClientQuickCallout() {
        if (
            !resolvedOrganizationId ||
            resolvedClientName === null ||
            selectedClientShiftIds.length === 0
        ) {
            return;
        }

        posthog?.capture(EVENTS.EXTENSION_CLIENT_QUICK_OUTREACH_CREATED, {
            client_name: resolvedClientName,
            shift_count: selectedClientShiftIds.length,
            provider: pageInfo?.provider ?? null,
        });
        openCreateOutreachInPhoebe(
            selectedClientShiftIds,
            'client_create_outreach',
        );
    }

    function toggleClientShiftSelection(shiftId: string, checked: boolean) {
        setSelectedClientShiftIds((currentSelectedShiftIds) => {
            const nextSelectedShiftIdSet = new Set(currentSelectedShiftIds);
            if (checked) {
                nextSelectedShiftIdSet.add(shiftId);
            } else {
                nextSelectedShiftIdSet.delete(shiftId);
            }

            return clientEligibleShiftIds.filter((candidateShiftId) =>
                nextSelectedShiftIdSet.has(candidateShiftId),
            );
        });
    }

    function handleToggleEhrSelectedShift(
        shift: SelectedExtensionShift,
        checked: boolean,
    ) {
        window.parent.postMessage(
            {
                type: 'EXTENSION_TOGGLE_SELECTED_SHIFT',
                payload: {
                    provider: shift.provider,
                    ...buildShiftPostMessageIdentity(shift),
                    sourceUrl: shift.sourceUrl,
                },
            },
            '*',
        );
        setEhrSelectedShifts((currentSelectedShifts) => {
            const shiftIdentityKey = buildShiftIdentityKey(shift);
            const alreadySelected = currentSelectedShifts.some(
                (currentShift) =>
                    buildShiftIdentityKey(currentShift) === shiftIdentityKey,
            );
            if (checked) {
                if (alreadySelected) {
                    return currentSelectedShifts;
                }

                const nextSelectedShiftKeySet = new Set(
                    currentSelectedShifts.map((currentShift) =>
                        buildShiftIdentityKey(currentShift),
                    ),
                );
                const shiftsToAdd = buildInitialSelectedEhrShiftGroup({
                    shift,
                    currentSelectedShifts,
                    pageShifts,
                });
                const nextSelectedShifts = [...currentSelectedShifts];
                for (const nextShift of shiftsToAdd) {
                    const nextShiftKey = buildShiftIdentityKey(nextShift);
                    if (nextSelectedShiftKeySet.has(nextShiftKey)) {
                        continue;
                    }

                    nextSelectedShiftKeySet.add(nextShiftKey);
                    nextSelectedShifts.push(nextShift);
                }

                return nextSelectedShifts;
            }

            if (!alreadySelected) {
                return currentSelectedShifts;
            }

            return currentSelectedShifts.filter(
                (currentShift) =>
                    buildShiftIdentityKey(currentShift) !== shiftIdentityKey,
            );
        });
    }

    function handleRemoveEhrSelectedShift(
        shift: Pick<
            SelectedExtensionShift,
            'externalShiftId' | 'wellskyShiftUniqueId'
        >,
    ) {
        window.parent.postMessage(
            {
                type: 'EXTENSION_REMOVE_SELECTED_SHIFT',
                payload: buildShiftPostMessageIdentity(shift),
            },
            '*',
        );
        const shiftIdentityKey = buildShiftIdentityKey(shift);
        setEhrSelectedShifts((current) =>
            current.filter(
                (s) => buildShiftIdentityKey(s) !== shiftIdentityKey,
            ),
        );
    }

    function handleClearEhrSelectedShifts() {
        window.parent.postMessage(
            { type: 'EXTENSION_CLEAR_SELECTED_SHIFTS' },
            '*',
        );
        setEhrSelectedShifts([]);
    }

    function handleOpenInPhoebeFromEhrSelection() {
        // The button is disabled while the selection holds a cancelled shift;
        // the coordinator must remove it rather than have it silently dropped
        // from an outreach they thought they were creating.
        if (hasCancelledSelectedShift) {
            return;
        }
        const resolvedShiftIds = batchResolvedItems
            .filter(
                (item) =>
                    item.status === 'resolved' && item.phoebeShiftId !== null,
            )
            .map((item) => item.phoebeShiftId as string);

        if (resolvedShiftIds.length === 0 || !resolvedOrganizationId) {
            return;
        }

        openCreateOutreachInPhoebe(resolvedShiftIds, 'ehr_multi_select');
        handleClearEhrSelectedShifts();
    }

    function handleRetryEhrSelectedShiftResolution() {
        setBatchResolveRetryNonce((currentNonce) => currentNonce + 1);
    }

    function handleOpenPageShiftInPhoebe(shift: ExtensionPageShift) {
        window.parent.postMessage(
            {
                type: 'EXTENSION_OPEN_PHOEBE_SHIFT',
                payload: {
                    provider: shift.provider,
                    ...buildShiftPostMessageIdentity(shift),
                    sourceUrl: shift.sourceUrl,
                    activeOutreachId: shift.activeOutreachId ?? undefined,
                    matchKey: shift.matchKey,
                },
            },
            '*',
        );
    }

    const shiftContextSummary = contextualShift ? (
        <div className="space-y-2">
            {!isSelectingEhrShifts ? (
                <div className={EXTENSION_EYEBROW_CLASS}>
                    Viewing this shift
                </div>
            ) : null}
            <p className="text-foreground text-[15px] font-semibold">
                {contextualShift.patient_name}
            </p>
            <p className="text-muted-foreground text-sm">
                {formatDate(contextualShift.shift_start_time, timezone)}{' '}
                &middot;{' '}
                {formatTime(contextualShift.shift_start_time, timezone)} -{' '}
                {formatTime(contextualShift.shift_end_time, timezone)}
            </p>
            <div className="flex flex-wrap gap-1.5">
                <Badge className={EXTENSION_META_BADGE_CLASS} variant="outline">
                    {formatShiftStatusLabel(contextualShift.status)}
                </Badge>
                <Badge className={EXTENSION_META_BADGE_CLASS} variant="outline">
                    {contextualShift.caregiver ? (
                        contextualShift.caregiver.name
                    ) : (
                        <span className="inline-flex items-center gap-2">
                            <span
                                className="h-1.5 w-1.5 rounded-full bg-amber-500"
                                aria-hidden="true"
                            />
                            <span>Unassigned</span>
                        </span>
                    )}
                </Badge>
                {contextualShift.frequency !== 'once' ? (
                    <Badge
                        className={cn(EXTENSION_META_BADGE_CLASS, 'capitalize')}
                        variant="outline"
                    >
                        {contextualShift.frequency.replace(/_/g, ' ')}
                    </Badge>
                ) : null}
            </div>
        </div>
    ) : null;
    const ehrSelectedShiftsSection =
        visibleEhrSelectedShifts.length > 0 ? (
            <section aria-label="Selected shifts" className="space-y-3">
                <div className={EXTENSION_EYEBROW_CLASS}>
                    Selected shifts ({visibleEhrSelectedShifts.length})
                </div>
                {selectedShiftResolutionState === 'waiting_for_context' ? (
                    <div
                        className={cn(
                            EXTENSION_SUBTLE_PANEL_CLASS,
                            'space-y-2 border-dashed',
                        )}
                    >
                        <div className="text-foreground flex items-center gap-2 text-sm font-medium">
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                            <span>Connecting selected shifts to Phoebe</span>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            Phoebe is still loading your organization context
                            before it can match these EHR shifts.
                        </p>
                    </div>
                ) : null}
                {selectedShiftResolutionState === 'resolving' ? (
                    <div
                        className={cn(
                            EXTENSION_SUBTLE_PANEL_CLASS,
                            'space-y-2 border-dashed',
                        )}
                    >
                        <div className="text-foreground flex items-center gap-2 text-sm font-medium">
                            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                            <span>Resolving selected shifts</span>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            Matching these EHR shifts to Phoebe records so the
                            batch action can open the right outreach flow.
                        </p>
                    </div>
                ) : null}
                {selectedShiftResolutionState === 'error' ? (
                    <div className="space-y-3 rounded-2xl border border-red-200 bg-red-50/80 p-4 dark:border-red-900 dark:bg-red-950/50">
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-red-900 dark:text-red-300">
                                Could not resolve selected shifts
                            </p>
                            <p className="text-sm text-red-700 dark:text-red-300">
                                {batchResolveError}
                            </p>
                        </div>
                        <Button
                            className="w-fit"
                            onClick={handleRetryEhrSelectedShiftResolution}
                            size="sm"
                            type="button"
                            variant="outline"
                        >
                            Retry
                        </Button>
                    </div>
                ) : null}
                {commonOtherOrgSuggestion !== null ? (
                    <div className="space-y-2 rounded-2xl border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-900 dark:bg-blue-950/50">
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                {commonOtherOrgSuggestion.count === 1
                                    ? 'This shift lives in another org'
                                    : `${commonOtherOrgSuggestion.count} of these shifts live in another org`}
                            </p>
                            <p className="text-xs text-blue-800/80 dark:text-blue-300/80">
                                Switch the extension to{' '}
                                {commonOtherOrgSuggestion.organizationName} to
                                act on{' '}
                                {commonOtherOrgSuggestion.count === 1
                                    ? 'it'
                                    : 'them'}
                                .
                            </p>
                        </div>
                        <Button
                            className="w-fit"
                            onClick={() => {
                                handleSelectExtensionOrganization(
                                    commonOtherOrgSuggestion.organizationId,
                                );
                            }}
                            size="sm"
                            type="button"
                            variant="outline"
                        >
                            Switch to{' '}
                            {commonOtherOrgSuggestion.organizationName}
                        </Button>
                    </div>
                ) : null}
                {commonOtherOrgMatches.length >= 2 ? (
                    <div
                        className="space-y-2 rounded-2xl border border-blue-200 bg-blue-50/70 p-3 dark:border-blue-900 dark:bg-blue-950/50"
                        data-testid="shift-selection-multi-org-suggestion"
                    >
                        <div className="space-y-1">
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                These shifts live in{' '}
                                {commonOtherOrgMatches.length} of your other
                                offices
                            </p>
                            <p className="text-xs text-blue-800/80 dark:text-blue-300/80">
                                Switch the extension to any of these offices to
                                act on them.
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {commonOtherOrgMatches.map((match) => (
                                <Button
                                    className="w-fit"
                                    key={match.organizationId}
                                    onClick={() => {
                                        handleSelectExtensionOrganization(
                                            match.organizationId,
                                        );
                                    }}
                                    size="sm"
                                    type="button"
                                    variant="outline"
                                >
                                    Switch to {match.organizationName}
                                </Button>
                            ))}
                        </div>
                    </div>
                ) : null}
                <div className="space-y-2">
                    {visibleEhrSelectedShifts.map((shift) => {
                        const resolved = batchResolvedItemsByIdentityKey.get(
                            buildShiftIdentityKey(shift),
                        );
                        const isNotFound = resolved?.status === 'not_found';
                        const isOtherOrg = resolved?.status === 'other_org';
                        const isMissing = resolved?.status === 'missing';
                        const isCancelled =
                            resolved?.status === 'resolved' &&
                            resolved.shiftStatus === 'cancelled';
                        const pageShiftForShift = pageShiftByIdentityKey.get(
                            buildShiftIdentityKey(shift),
                        );

                        return (
                            <div
                                className={cn(
                                    'bg-background flex items-start gap-2 rounded-xl border p-3',
                                    isCancelled
                                        ? 'border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/50'
                                        : isNotFound
                                          ? 'border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/50'
                                          : isOtherOrg
                                            ? 'border-blue-200 bg-blue-50/40 dark:border-blue-900 dark:bg-blue-950/50'
                                            : isMissing ||
                                                selectedShiftResolutionState ===
                                                    'error'
                                              ? 'border-red-200 bg-red-50/40 dark:border-red-900 dark:bg-red-950/50'
                                              : 'border-border/80',
                                )}
                                key={buildShiftIdentityKey(shift)}
                            >
                                <div className="min-w-0 flex-1 space-y-0.5">
                                    {resolved?.status === 'resolved' ? (
                                        <Fragment>
                                            <p className="text-foreground text-sm font-medium">
                                                {resolved.patientName ??
                                                    'Unknown client'}
                                            </p>
                                            <p className="text-muted-foreground text-xs">
                                                {resolved.shiftStartTime
                                                    ? `${formatDate(resolved.shiftStartTime, timezone)} ${formatTime(resolved.shiftStartTime, timezone)}`
                                                    : ''}{' '}
                                                &ndash;{' '}
                                                {resolved.shiftEndTime
                                                    ? formatTime(
                                                          resolved.shiftEndTime,
                                                          timezone,
                                                      )
                                                    : ''}
                                            </p>
                                            {isCancelled ? (
                                                <p className="text-xs font-medium text-red-700 dark:text-red-300">
                                                    Cancelled. Remove this shift
                                                    to continue.
                                                </p>
                                            ) : null}
                                        </Fragment>
                                    ) : isNotFound ? (
                                        <div className="space-y-1">
                                            {pageShiftForShift ? (
                                                <Fragment>
                                                    <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                                                        {
                                                            pageShiftForShift.title
                                                        }
                                                    </p>
                                                    {pageShiftForShift.subtitle ? (
                                                        <p className="text-xs text-amber-800/80 dark:text-amber-300/80">
                                                            {
                                                                pageShiftForShift.subtitle
                                                            }
                                                        </p>
                                                    ) : null}
                                                </Fragment>
                                            ) : (
                                                <p className="text-sm font-medium text-amber-900 dark:text-amber-300">
                                                    Not synced to Phoebe yet
                                                </p>
                                            )}
                                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                                This shift isn&rsquo;t in Phoebe
                                                yet. The bulk action will skip
                                                it.
                                            </p>
                                        </div>
                                    ) : isOtherOrg ? (
                                        <div className="space-y-1.5">
                                            {pageShiftForShift ? (
                                                <Fragment>
                                                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                                        {
                                                            pageShiftForShift.title
                                                        }
                                                    </p>
                                                    {pageShiftForShift.subtitle ? (
                                                        <p className="text-xs text-blue-800/80 dark:text-blue-300/80">
                                                            {
                                                                pageShiftForShift.subtitle
                                                            }
                                                        </p>
                                                    ) : null}
                                                </Fragment>
                                            ) : (
                                                <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                                    Lives in another org
                                                </p>
                                            )}
                                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                                {resolved.otherOrgMatches
                                                    .length === 1 ? (
                                                    <Fragment>
                                                        In{' '}
                                                        {resolved
                                                            .otherOrgMatches[0]
                                                            ?.organizationName ??
                                                            'another organization'}
                                                        .{' '}
                                                    </Fragment>
                                                ) : (
                                                    <Fragment>
                                                        Exists in{' '}
                                                        {
                                                            resolved
                                                                .otherOrgMatches
                                                                .length
                                                        }{' '}
                                                        of your orgs.{' '}
                                                    </Fragment>
                                                )}
                                                {commonOtherOrgSuggestion ===
                                                    null &&
                                                commonOtherOrgMatches.length < 2
                                                    ? 'Switch the extension to act on it.'
                                                    : 'The bulk action will skip it.'}
                                            </p>
                                            {commonOtherOrgSuggestion ===
                                                null &&
                                            commonOtherOrgMatches.length < 2 ? (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {resolved.otherOrgMatches.map(
                                                        (match) => (
                                                            <Button
                                                                className="h-7 px-2 text-xs"
                                                                key={
                                                                    match.organizationId
                                                                }
                                                                onClick={() => {
                                                                    handleSelectExtensionOrganization(
                                                                        match.organizationId,
                                                                    );
                                                                }}
                                                                size="sm"
                                                                type="button"
                                                                variant="outline"
                                                            >
                                                                Switch to{' '}
                                                                {match.organizationName ??
                                                                    'this org'}
                                                            </Button>
                                                        ),
                                                    )}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : isMissing ? (
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium text-red-800 dark:text-red-300">
                                                Phoebe did not return a result
                                                for this shift
                                            </p>
                                            <p className="font-mono text-xs text-red-700 dark:text-red-300">
                                                {shift.externalShiftId}
                                            </p>
                                        </div>
                                    ) : selectedShiftResolutionState ===
                                      'error' ? (
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium text-red-800 dark:text-red-300">
                                                Shift lookup failed
                                            </p>
                                            <p className="font-mono text-xs text-red-700 dark:text-red-300">
                                                {shift.externalShiftId}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-2 py-0.5">
                                            <div className="bg-muted/80 h-4 w-28 animate-pulse rounded-full" />
                                            <div className="bg-muted h-3 w-44 animate-pulse rounded-full" />
                                            <p className="text-muted-foreground/70 font-mono text-[11px]">
                                                {shift.externalShiftId}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <button
                                    aria-label={`Remove shift ${shift.externalShiftId}`}
                                    className="text-muted-foreground/70 hover:bg-accent hover:text-muted-foreground flex-none rounded p-0.5"
                                    onClick={() => {
                                        handleRemoveEhrSelectedShift(shift);
                                    }}
                                    type="button"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-col gap-2">
                    {isExtensionAgentChatEnabled ? (
                        <Button
                            className={cn(
                                'w-full',
                                EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                            )}
                            disabled={
                                selectedShiftResolutionState ===
                                    'waiting_for_context' ||
                                isBatchResolving ||
                                resolvedSelectedShiftCount === 0
                            }
                            onClick={handleOpenAgentChat}
                            size="sm"
                            type="button"
                        >
                            <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                            {resolvedSelectedShiftCount === 1
                                ? 'Ask Phoebe about this shift'
                                : `Ask Phoebe about these ${resolvedSelectedShiftCount} shifts`}
                        </Button>
                    ) : null}
                    <div className="flex gap-2">
                        {hasCancelledSelectedShift ? (
                            <p
                                className="sr-only"
                                id="extension-cancelled-selection-reason"
                            >
                                One or more selected shifts are cancelled.
                                Remove them to continue.
                            </p>
                        ) : null}
                        <Button
                            aria-describedby={
                                hasCancelledSelectedShift
                                    ? 'extension-cancelled-selection-reason'
                                    : undefined
                            }
                            className={cn(
                                'flex-1',
                                !isExtensionAgentChatEnabled &&
                                    EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                            )}
                            disabled={
                                selectedShiftResolutionState ===
                                    'waiting_for_context' ||
                                isBatchResolving ||
                                hasCancelledSelectedShift ||
                                resolvedSelectedShiftCount === 0
                            }
                            onClick={handleOpenInPhoebeFromEhrSelection}
                            size="sm"
                            type="button"
                            variant={
                                isExtensionAgentChatEnabled
                                    ? 'outline'
                                    : undefined
                            }
                        >
                            {selectedShiftResolutionState ===
                            'waiting_for_context' ? (
                                <Fragment>
                                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    Connecting&hellip;
                                </Fragment>
                            ) : isBatchResolving ? (
                                <Fragment>
                                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                                    Resolving&hellip;
                                </Fragment>
                            ) : (
                                'Find caregivers'
                            )}
                        </Button>
                        <Button
                            className="text-xs"
                            onClick={handleClearEhrSelectedShifts}
                            size="sm"
                            type="button"
                            variant="ghost"
                        >
                            Clear
                        </Button>
                    </div>
                </div>
            </section>
        ) : null;
    const shouldShowNoPageShiftsMessage =
        pageShifts.length === 0 &&
        (visibleEhrSelectedShifts.length > 0 ||
            pageInfo?.entityType === 'shifts');
    const pageShiftCards = (
        shifts: ReadonlyArray<ExtensionPageShift>,
        sectionLabel?: string | null,
    ) => {
        if (shifts.length === 0) {
            return null;
        }

        return (
            <section className="space-y-3">
                {sectionLabel ? (
                    <div className={EXTENSION_EYEBROW_CLASS}>
                        {sectionLabel}
                    </div>
                ) : null}
                <div className="space-y-2">
                    {shifts.map((shift) => {
                        const isSelectable =
                            effectiveUseMultiShiftCalloutResponse &&
                            shift.variant === 'fill' &&
                            shift.activeOutreachId === null;
                        const isSelected = selectedEhrShiftIdentityKeySet.has(
                            buildShiftIdentityKey(shift),
                        );

                        return (
                            <div
                                className={cn(
                                    EXTENSION_CARD_CLASS,
                                    'space-y-3 p-3',
                                    isSelected
                                        ? EXTENSION_SELECTED_CARD_CLASS
                                        : null,
                                )}
                                key={buildShiftIdentityKey(shift)}
                            >
                                {(() => {
                                    const resolved = pageShiftResolvedMap.get(
                                        buildShiftIdentityKey(shift),
                                    );
                                    const displayTitle =
                                        resolved?.patientName ?? shift.title;
                                    const displaySubtitle =
                                        resolved?.shiftStartTime
                                            ? `${formatDate(resolved.shiftStartTime, timezone)} ${formatTime(resolved.shiftStartTime, timezone)}` +
                                              (resolved.shiftEndTime
                                                  ? ` \u2013 ${formatTime(resolved.shiftEndTime, timezone)}`
                                                  : '')
                                            : shift.subtitle;
                                    return (
                                        <div className="space-y-1">
                                            <p className="text-foreground text-sm font-semibold">
                                                {displayTitle}
                                            </p>
                                            {displaySubtitle ? (
                                                <p className="text-muted-foreground text-sm">
                                                    {displaySubtitle}
                                                </p>
                                            ) : null}
                                            {shift.details ? (
                                                <p className="text-muted-foreground text-xs">
                                                    {shift.details}
                                                </p>
                                            ) : null}
                                        </div>
                                    );
                                })()}
                                <div className="flex items-center gap-2">
                                    {isSelectable ? (
                                        <label
                                            className={cn(
                                                'border-border bg-muted flex min-h-9 min-w-9 items-center justify-center rounded-lg border px-2',
                                                isSelected
                                                    ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950/50'
                                                    : 'hover:border-muted-foreground',
                                            )}
                                        >
                                            <Checkbox
                                                aria-label={`Select ${shift.title}`}
                                                checked={isSelected}
                                                onCheckedChange={(checked) => {
                                                    handleToggleEhrSelectedShift(
                                                        shift,
                                                        checked === true,
                                                    );
                                                }}
                                            />
                                        </label>
                                    ) : null}
                                    <Button
                                        className={cn(
                                            'flex-1',
                                            isSelectable && isSelected
                                                ? EXTENSION_SELECTED_ACTION_BUTTON_CLASS
                                                : EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                        )}
                                        onClick={() => {
                                            if (isSelectable) {
                                                handleToggleEhrSelectedShift(
                                                    shift,
                                                    !isSelected,
                                                );
                                                return;
                                            }
                                            handleOpenPageShiftInPhoebe(shift);
                                        }}
                                        size="sm"
                                        type="button"
                                    >
                                        {isSelectable && isSelected
                                            ? '✓ Selected'
                                            : shift.label}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        );
    };
    const outreachTabCurrentShiftSection =
        visibleCurrentPageShift && !contextualShiftFeedItem
            ? pageShiftCards([visibleCurrentPageShift], 'Viewing this shift')
            : null;
    const conversationWorkspace = (
        <div className="-mx-4 h-[calc(100vh-174px)] overflow-hidden">
            {selectedConversationId ? (
                <OrganizationConversationDetailPane
                    conversationId={selectedConversationId}
                    markAsRead={markAsRead}
                    markAsUnread={markAsUnread}
                    messageAnalyticsSurface="extension"
                    onBack={() => {
                        selectedConversationSourceRef.current = null;
                        setSelectedConversationId(null);
                    }}
                    onOpenInPhoebe={() => {
                        openConversationInPhoebe(selectedConversationId);
                    }}
                    organizationId={resolvedOrganizationId ?? ''}
                    selectedConversation={selectedSidebarConversation}
                />
            ) : (
                <OrganizationConversationListPanel
                    conversations={sidebarConversations}
                    hasMore={!isReachingEndSidebarConversations}
                    isLoading={isLoadingSidebarConversations}
                    isLoadingMore={isLoadingMoreSidebarConversations}
                    isMarkingAllAsRead={isMarkingAllConversationsAsRead}
                    markAsRead={markAsRead}
                    markAsUnread={markAsUnread}
                    onLoadMore={loadMoreSidebarConversations}
                    onMarkAllAsRead={handleMarkAllSidebarConversationsAsRead}
                    onSelectConversation={selectConversationManually}
                    pendingFirst={conversationPendingFirst}
                    pinnedConversation={contextualSidebarConversation}
                    pinnedConversationLabel="Current caregiver"
                    search={conversationSearch}
                    setPendingFirst={setConversationPendingFirst}
                    setSearch={setConversationSearch}
                    title={null}
                    totalPendingCount={sidebarPendingMessagesCount ?? 0}
                    totalUnreadCount={sidebarUnreadCount ?? 0}
                />
            )}
        </div>
    );

    return (
        <div className="bg-muted/20 relative min-h-screen">
            <NotificationTabTitle />
            <header className="bg-background border-b px-4 py-3">
                <div className="flex items-center gap-2">
                    <div className="flex min-h-8 items-center">
                        <NotificationBell
                            clickBehavior="new-tab"
                            showSettingsLink={true}
                            telemetrySurface="extension"
                        />
                    </div>
                    <div className="flex min-w-0 flex-1 items-center justify-center">
                        {renderOrganizationPicker({
                            pickerId: 'header',
                            triggerClassName: 'w-full justify-between px-3',
                            triggerWidthClassName: 'w-full',
                        })}
                    </div>
                    <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        aria-label="Open settings"
                        onClick={() => {
                            setIsSettingsOpen(true);
                        }}
                    >
                        <Settings className="h-4 w-4" />
                    </Button>
                </div>
            </header>
            {shouldShowStoreReleaseReminder ? (
                <section className="border-b border-[#d8ccff] bg-[#f5f1ff] px-4 py-3 text-[#24124d]">
                    <div className="space-y-2">
                        <div>
                            <p className="text-sm font-semibold">
                                You are using a test build of the Phoebe Chrome
                                extension.
                            </p>
                            <p className="mt-0.5 text-xs text-[#5f4f89]">
                                Install the latest release so updates arrive
                                automatically.
                            </p>
                        </div>
                        <Button
                            asChild={true}
                            size="sm"
                            className="h-8 bg-lime-300 text-[#24124d] hover:bg-lime-200 hover:text-[#24124d]"
                        >
                            <a
                                href={CHROME_EXTENSION_INSTALL_URL}
                                rel="noreferrer"
                                target="_blank"
                            >
                                Install from Chrome Web Store
                            </a>
                        </Button>
                    </div>
                </section>
            ) : null}
            <Dialog
                open={isSettingsOpen}
                onOpenChange={(open) => {
                    setIsSettingsOpen(open);
                    if (!open) {
                        setOpenOrganizationPickerId(null);
                    }
                }}
            >
                <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-sm:top-[50%] max-sm:bottom-auto max-sm:left-[50%] max-sm:max-w-[calc(100vw-2rem)] max-sm:translate-x-[-50%] max-sm:translate-y-[-50%] max-sm:rounded-lg max-sm:p-6">
                    <DialogHeader>
                        <DialogTitle>Settings</DialogTitle>
                        <DialogDescription>
                            Customize how Phoebe behaves in the extension
                            sidepanel.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6">
                        <section className="space-y-3">
                            <div className="rounded-lg border p-3">
                                <div className="space-y-1">
                                    <h2 className="text-sm font-semibold">
                                        Organization
                                    </h2>
                                    <p className="text-muted-foreground text-xs">
                                        Choose which organization Phoebe should
                                        use in this sidepanel.
                                    </p>
                                </div>
                                {showOrganizationPicker ? (
                                    <div className="mt-3">
                                        {renderOrganizationPicker({
                                            pickerId: 'settings',
                                            triggerClassName:
                                                'w-full justify-between px-3',
                                            triggerWidthClassName: 'w-full',
                                        })}
                                    </div>
                                ) : (
                                    <p className="mt-3 text-sm font-medium">
                                        {resolvedOrganizationName ??
                                            'No organization selected'}
                                    </p>
                                )}
                            </div>
                        </section>
                        <section className="space-y-3">
                            <Separator />
                            <div>
                                <h2 className="text-sm font-semibold">
                                    Notifications
                                </h2>
                                <p className="text-muted-foreground text-xs">
                                    Manage notification preferences in the full
                                    settings page.
                                </p>
                            </div>
                            <Button
                                asChild={true}
                                variant="outline"
                                size="sm"
                                className="w-full justify-start"
                            >
                                <a
                                    href="/settings"
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    Open settings
                                </a>
                            </Button>
                        </section>
                        <div className="space-y-3">
                            <Separator />
                            <section className="space-y-3">
                                <div>
                                    <h2 className="text-sm font-semibold">
                                        Appearance
                                    </h2>
                                    <p className="text-muted-foreground text-xs">
                                        Adjust what the extension sidepanel
                                        shows by default.
                                    </p>
                                </div>
                                <div className="space-y-3 rounded-lg border p-3">
                                    {EXTENSION_BOTTOM_BAR_ENABLED ? (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="space-y-0.5">
                                                <p className="text-sm font-medium">
                                                    Show bottom bar
                                                </p>
                                                <p className="text-muted-foreground text-xs">
                                                    Keep the quick outreach bar
                                                    pinned to the bottom of the
                                                    sidepanel.
                                                </p>
                                            </div>
                                            <Switch
                                                aria-label="Show bottom bar"
                                                checked={showBottomBar}
                                                onCheckedChange={
                                                    handleToggleBottomBar
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    {EXTENSION_MINIMIZED_RAIL_ENABLED ? (
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="space-y-0.5">
                                                <p className="text-sm font-medium">
                                                    Show minimized rail
                                                </p>
                                                <p className="text-muted-foreground text-xs">
                                                    Display the Phoebe rail on
                                                    supported EHR pages.
                                                </p>
                                            </div>
                                            <Switch
                                                aria-label="Show minimized rail"
                                                checked={showMinimizedRail}
                                                onCheckedChange={
                                                    handleToggleMinimizedRail
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="space-y-0.5">
                                            <p className="text-sm font-medium">
                                                Keep sidebar open across EHR
                                                pages
                                            </p>
                                            <p className="text-muted-foreground text-xs">
                                                Reuse the open sidebar state
                                                when you move between pages on
                                                the same EHR domain.
                                            </p>
                                        </div>
                                        <Switch
                                            aria-label="Keep sidebar open across EHR pages"
                                            checked={
                                                syncSidebarOpenStateByDomain
                                            }
                                            onCheckedChange={
                                                handleToggleSidebarOpenStateSync
                                            }
                                        />
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <main
                className={cn(
                    'p-4',
                    activeTab === 'outreaches' &&
                        shouldShowSelectableClientShiftCards
                        ? 'pb-28'
                        : null,
                    // Clear the fixed "Ask Phoebe" bar when it's visible.
                    isExtensionAgentChatEnabled &&
                        !isAgentChatOpen &&
                        !shouldShowSelectableClientShiftCards &&
                        'pb-20',
                )}
            >
                <Tabs
                    className="gap-4"
                    onValueChange={(value) => {
                        if (
                            value === 'outreaches' ||
                            value === 'conversations' ||
                            value === 'page_shifts'
                        ) {
                            isConversationWorkspacePinnedByUserRef.current =
                                value === 'conversations';
                            setActiveTab(value);
                        }
                    }}
                    value={activeTab}
                >
                    <TabsList className="bg-muted grid h-auto w-full grid-cols-3 rounded-2xl p-1">
                        <TabsTrigger
                            className="rounded-xl text-sm"
                            value="page_shifts"
                        >
                            Shifts
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-xl text-sm"
                            value="outreaches"
                        >
                            Outreaches
                        </TabsTrigger>
                        <TabsTrigger
                            className="rounded-xl text-sm"
                            value="conversations"
                        >
                            Conversations
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent className="space-y-4" value="outreaches">
                        {outreachTabCurrentShiftSection ? (
                            outreachTabCurrentShiftSection
                        ) : shiftContextSummary ? (
                            contextualShiftFeedItem ? (
                                <div className="space-y-2">
                                    {!isSelectingEhrShifts ? (
                                        <div
                                            className={EXTENSION_EYEBROW_CLASS}
                                        >
                                            Viewing this shift
                                        </div>
                                    ) : null}
                                    {selectedShiftFeedCard}
                                </div>
                            ) : contextualShiftPrimaryAction ||
                              outreachTabShiftPrimaryAction ||
                              shouldShowShiftNoOutreachBanner ? (
                                <div
                                    className={cn(
                                        EXTENSION_CARD_CLASS,
                                        'overflow-hidden',
                                    )}
                                >
                                    <div className="bg-muted/70 p-4">
                                        {shiftContextSummary}
                                    </div>
                                    <div className="border-border space-y-3 border-t bg-stone-50/80 p-4">
                                        {outreachTabShiftPrimaryAction ? (
                                            <Fragment>
                                                <div className="space-y-1">
                                                    <p className="text-foreground text-sm font-semibold">
                                                        {
                                                            outreachTabShiftPrimaryAction.title
                                                        }
                                                    </p>
                                                    <p className="text-muted-foreground text-sm">
                                                        {
                                                            outreachTabShiftPrimaryAction.description
                                                        }
                                                    </p>
                                                </div>
                                                <Button
                                                    className={cn(
                                                        'w-full',
                                                        EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                                    )}
                                                    onClick={() => {
                                                        outreachTabShiftPrimaryAction.onClick();
                                                    }}
                                                    size="sm"
                                                    type="button"
                                                >
                                                    {
                                                        outreachTabShiftPrimaryAction.label
                                                    }
                                                </Button>
                                            </Fragment>
                                        ) : (
                                            <div className="space-y-1">
                                                <p className="text-foreground text-sm font-semibold">
                                                    No active outreach for this
                                                    shift
                                                </p>
                                                <p className="text-muted-foreground text-sm">
                                                    Starting outreach is only
                                                    available for unassigned
                                                    shifts.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className={EXTENSION_CONTEXT_CARD_CLASS}>
                                    {shiftContextSummary}
                                </div>
                            )
                        ) : null}
                        {!contextualShift && resolvedClientName ? (
                            <div
                                className={cn(
                                    EXTENSION_CONTEXT_CARD_CLASS,
                                    'space-y-2',
                                )}
                            >
                                <div className={EXTENSION_EYEBROW_CLASS}>
                                    Viewing this client
                                </div>
                                <p className="text-foreground text-[15px] font-semibold">
                                    {resolvedClientName}
                                </p>
                            </div>
                        ) : null}
                        {shouldShowClientQuickCalloutPrompt ? (
                            shouldUseClientQuickCalloutFallback &&
                            clientCreateOutreachUrl ? (
                                <div
                                    className={cn(
                                        EXTENSION_CARD_CLASS,
                                        'space-y-3 p-4',
                                    )}
                                >
                                    <div className="space-y-1">
                                        <p className="text-foreground text-sm font-semibold">
                                            {orderedClientEligibleShifts.length}{' '}
                                            open shift
                                            {orderedClientEligibleShifts.length ===
                                            1
                                                ? ''
                                                : 's'}{' '}
                                            for {resolvedClientName}
                                        </p>
                                        <p className="text-muted-foreground text-sm">
                                            Open Phoebe to pick which shifts to
                                            include.
                                        </p>
                                    </div>
                                    <Button
                                        asChild={true}
                                        className={
                                            EXTENSION_PRIMARY_ACTION_BUTTON_CLASS
                                        }
                                        onClick={() => {
                                            posthog?.capture(
                                                EVENTS.EXTENSION_OPEN_IN_PHOEBE_CLICKED,
                                                {
                                                    shift_count:
                                                        orderedClientEligibleShifts.length,
                                                    source: 'client_context_select_shifts',
                                                },
                                            );
                                            posthog?.capture(
                                                EVENTS.EXTENSION_CLIENT_QUICK_OUTREACH_CREATED,
                                                {
                                                    client_name:
                                                        resolvedClientName,
                                                    shift_count:
                                                        orderedClientEligibleShifts.length,
                                                    provider:
                                                        pageInfo?.provider ??
                                                        null,
                                                    source: 'client_context_select_shifts',
                                                },
                                            );
                                        }}
                                        size="sm"
                                        variant="outline"
                                    >
                                        <a
                                            href={clientCreateOutreachUrl}
                                            rel="noreferrer"
                                            target="_blank"
                                        >
                                            Open in Phoebe to select shifts
                                        </a>
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {orderedClientEligibleShifts.map(
                                        (shift) => {
                                            const checkboxId = `client-shift-${shift.id}`;
                                            const isSelected =
                                                selectedClientShiftIdSet.has(
                                                    shift.id,
                                                );

                                            return (
                                                <label
                                                    className={cn(
                                                        'border-border/80 bg-background block cursor-pointer rounded-2xl border p-4 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-colors',
                                                        isSelected
                                                            ? EXTENSION_SELECTED_CARD_CLASS
                                                            : 'hover:border-border hover:bg-muted/40',
                                                    )}
                                                    htmlFor={checkboxId}
                                                    key={shift.id}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className="min-w-0 flex-1 space-y-1">
                                                            <p className="text-sm font-medium">
                                                                {formatDate(
                                                                    shift.shift_start_time,
                                                                    timezone,
                                                                )}{' '}
                                                                &middot;{' '}
                                                                {formatTime(
                                                                    shift.shift_start_time,
                                                                    timezone,
                                                                )}{' '}
                                                                -{' '}
                                                                {formatTime(
                                                                    shift.shift_end_time,
                                                                    timezone,
                                                                )}
                                                            </p>
                                                            <Badge
                                                                className={
                                                                    EXTENSION_META_BADGE_CLASS
                                                                }
                                                                variant="outline"
                                                            >
                                                                {formatShiftStatusLabel(
                                                                    shift.status,
                                                                )}
                                                            </Badge>
                                                        </div>
                                                        <Checkbox
                                                            checked={isSelected}
                                                            id={checkboxId}
                                                            onCheckedChange={(
                                                                checked,
                                                            ) => {
                                                                toggleClientShiftSelection(
                                                                    shift.id,
                                                                    checked ===
                                                                        true,
                                                                );
                                                            }}
                                                        />
                                                    </div>
                                                </label>
                                            );
                                        },
                                    )}
                                </div>
                            )
                        ) : null}
                        {isResolvingShiftContext && !contextualShift ? (
                            <div className="border-border bg-background/70 space-y-1 rounded-2xl border border-dashed p-4">
                                <p className="text-foreground text-sm font-medium">
                                    Looking up this shift in Phoebe
                                </p>
                                <p className="text-muted-foreground text-sm">
                                    The feed will pin the matching outreach if
                                    one exists.
                                </p>
                            </div>
                        ) : null}
                        {isResolvingClientContext && !resolvedClientName ? (
                            <div className="border-border bg-background/70 space-y-1 rounded-2xl border border-dashed p-4">
                                <p className="text-foreground text-sm font-medium">
                                    Looking up this client in Phoebe
                                </p>
                                <p className="text-muted-foreground text-sm">
                                    The feed will pin likely matching outreaches
                                    if Phoebe can resolve the client.
                                </p>
                            </div>
                        ) : null}
                        {shouldShowClientSyncAction ? (
                            <div
                                className={cn(
                                    EXTENSION_SUBTLE_PANEL_CLASS,
                                    'space-y-3',
                                )}
                            >
                                <div className="space-y-1">
                                    <p className="text-foreground text-sm font-semibold">
                                        {isUnresolvedClientContext
                                            ? 'Client not synced to Phoebe'
                                            : 'Sync client with Phoebe'}
                                    </p>
                                    <p className="text-muted-foreground text-sm">
                                        {isUnresolvedClientContext
                                            ? 'Sync this client to load their Phoebe context and outreaches.'
                                            : 'Refresh this client’s details and shifts from the EHR.'}
                                    </p>
                                </div>
                                <Button
                                    className={cn(
                                        'w-full',
                                        EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                    )}
                                    disabled={isSyncingClient}
                                    onClick={() => {
                                        void handleSyncClient();
                                    }}
                                    size="sm"
                                    type="button"
                                >
                                    {isSyncingClient ? (
                                        <Fragment>
                                            <Loader2 className="mr-2 size-4 animate-spin" />
                                            Syncing client
                                        </Fragment>
                                    ) : (
                                        'Sync client'
                                    )}
                                </Button>
                            </div>
                        ) : null}
                        {shouldShowShiftNoOutreachBanner &&
                        !shiftContextSummary &&
                        !contextualShiftPrimaryAction ? (
                            <div
                                className={cn(
                                    EXTENSION_SUBTLE_PANEL_CLASS,
                                    'space-y-3',
                                )}
                            >
                                <div className="space-y-1">
                                    <p className="text-foreground text-sm font-semibold">
                                        No active outreach for this shift
                                    </p>
                                    <p className="text-muted-foreground text-sm">
                                        {shouldShowShiftQuickCalloutAction
                                            ? 'Start one?'
                                            : 'Starting outreach is only available for unassigned shifts.'}
                                    </p>
                                </div>
                                {shouldShowShiftQuickCalloutAction ? (
                                    <Button
                                        className={cn(
                                            'w-full',
                                            EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                        )}
                                        onClick={() => {
                                            handleCreateQuickOutreach();
                                        }}
                                        size="sm"
                                        type="button"
                                    >
                                        Find Caregivers
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                        {!outreachTabCurrentShiftSection &&
                        !shiftContextSummary &&
                        contextualShiftFeedItem
                            ? selectedShiftFeedCard
                            : null}
                        {!outreachTabCurrentShiftSection &&
                        !shiftContextSummary &&
                        !contextualShiftFeedItem &&
                        outreachTabShiftPrimaryAction ? (
                            <div
                                className={cn(
                                    EXTENSION_SUBTLE_PANEL_CLASS,
                                    'space-y-3',
                                )}
                            >
                                <div className="space-y-1">
                                    <p className="text-foreground text-sm font-semibold">
                                        {outreachTabShiftPrimaryAction.title}
                                    </p>
                                    <p className="text-muted-foreground text-sm">
                                        {
                                            outreachTabShiftPrimaryAction.description
                                        }
                                    </p>
                                </div>
                                <Button
                                    className={cn(
                                        'w-full',
                                        EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                    )}
                                    onClick={() => {
                                        outreachTabShiftPrimaryAction.onClick();
                                    }}
                                    size="sm"
                                    type="button"
                                >
                                    {outreachTabShiftPrimaryAction.label}
                                </Button>
                            </div>
                        ) : null}
                        {ehrSelectedShiftsSection}
                        <section className="space-y-3">
                            <div className="flex items-center justify-between gap-3">
                                <div className={EXTENSION_EYEBROW_CLASS}>
                                    Active Outreach Feed
                                </div>
                                <Popover
                                    onOpenChange={setIsFeedControlsOpen}
                                    open={isFeedControlsOpen}
                                >
                                    <PopoverTrigger asChild={true}>
                                        <Button
                                            aria-label="Filter and sort active outreach feed"
                                            className="border-border bg-background h-7 gap-1.5 px-2 text-xs shadow-none"
                                            size="sm"
                                            type="button"
                                            variant="outline"
                                        >
                                            <SlidersHorizontal className="size-3.5" />
                                            {showOnlyMineFeedFilter ? (
                                                <span className="size-1.5 rounded-full bg-violet-500" />
                                            ) : null}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="end"
                                        className="w-56 space-y-3 p-3"
                                    >
                                        <div className="space-y-2">
                                            <div
                                                className={cn(
                                                    EXTENSION_EYEBROW_CLASS,
                                                )}
                                            >
                                                Filter
                                            </div>
                                            <label className="text-foreground/80 flex cursor-pointer items-center gap-2 text-xs">
                                                <Checkbox
                                                    aria-label="Show only outreaches I created"
                                                    checked={
                                                        showOnlyMineFeedFilter
                                                    }
                                                    disabled={!user?.id}
                                                    onCheckedChange={(
                                                        checked,
                                                    ) => {
                                                        setShowOnlyMineFeedFilter(
                                                            checked === true,
                                                        );
                                                    }}
                                                />
                                                Created by me
                                            </label>
                                        </div>
                                        {shouldShowFeedSortControl ? (
                                            <div className="space-y-2">
                                                <div
                                                    className={cn(
                                                        EXTENSION_EYEBROW_CLASS,
                                                    )}
                                                >
                                                    Sort by
                                                </div>
                                                <Select
                                                    onValueChange={(value) => {
                                                        if (
                                                            isExtensionOutreachFeedSort(
                                                                value,
                                                            )
                                                        ) {
                                                            setFeedSort(value);
                                                        }
                                                    }}
                                                    value={feedSort}
                                                >
                                                    <SelectTrigger
                                                        aria-label="Sort active outreach feed"
                                                        className="border-border bg-background w-full text-xs shadow-none"
                                                        size="sm"
                                                    >
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {EXTENSION_OUTREACH_FEED_SORT_OPTIONS.map(
                                                            (option) => (
                                                                <SelectItem
                                                                    key={
                                                                        option.value
                                                                    }
                                                                    value={
                                                                        option.value
                                                                    }
                                                                >
                                                                    {
                                                                        option.label
                                                                    }
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ) : null}
                                    </PopoverContent>
                                </Popover>
                            </div>
                            {sortedPinnedHomeFeedItems.length > 0 &&
                            pinnedContextLabel ? (
                                <div className="space-y-2">
                                    <div
                                        className={cn(
                                            EXTENSION_EYEBROW_CLASS,
                                            'px-4',
                                        )}
                                    >
                                        {pinnedContextLabel}
                                    </div>
                                    <OutreachFeedCompact
                                        homeFeedItems={
                                            sortedPinnedHomeFeedItems
                                        }
                                        isLoading={false}
                                        organizationId={resolvedOrganizationId}
                                        onSelectOutreach={(outreachId) => {
                                            openOutreachInPhoebe(
                                                outreachId,
                                                'feed',
                                            );
                                        }}
                                        onStartCallout={
                                            openSuggestedCalloutInPhoebe
                                        }
                                    />
                                </div>
                            ) : null}
                            {sortedPinnedHomeFeedItems.length === 0 ||
                            sortedRemainingHomeFeedOutreaches.length > 0 ||
                            isInitializing ||
                            isLoadingHomeFeed ||
                            !!homeFeedError ? (
                                <OutreachFeedCompact
                                    error={homeFeedError}
                                    homeFeedItems={
                                        sortedRemainingHomeFeedOutreaches
                                    }
                                    isLoading={
                                        isInitializing || isLoadingHomeFeed
                                    }
                                    organizationId={resolvedOrganizationId}
                                    onSelectOutreach={(outreachId) => {
                                        openOutreachInPhoebe(
                                            outreachId,
                                            'feed',
                                        );
                                    }}
                                    onStartCallout={
                                        openSuggestedCalloutInPhoebe
                                    }
                                />
                            ) : null}
                        </section>
                        {shouldShowSelectableClientShiftCards ? (
                            <div className="pointer-events-none sticky bottom-0 -mx-4 px-4 pb-4">
                                <div className="bg-background/95 supports-[backdrop-filter]:bg-background/80 pointer-events-auto space-y-2 rounded-xl border p-3 shadow-lg backdrop-blur">
                                    <Button
                                        className={cn(
                                            'w-full',
                                            EXTENSION_PRIMARY_ACTION_BUTTON_CLASS,
                                        )}
                                        disabled={
                                            selectedClientShiftCount === 0
                                        }
                                        onClick={() => {
                                            handleCreateClientQuickCallout();
                                        }}
                                        type="button"
                                    >
                                        {`Find Caregivers for ${selectedClientShiftCount} shift${selectedClientShiftCount === 1 ? '' : 's'}`}
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </TabsContent>
                    <TabsContent className="space-y-4" value="page_shifts">
                        {ehrSelectedShiftsSection}
                        <ExtensionShiftRangeSection
                            isActive={activeTab === 'page_shifts'}
                            onFindCaregivers={(shiftId) => {
                                openCreateOutreachInPhoebe(
                                    [shiftId],
                                    'extension_shift_range',
                                );
                            }}
                            organizationId={resolvedOrganizationId}
                            refreshSignal={extensionShiftRangeRefreshSignal}
                            timezone={timezone}
                        />
                        {visibleCurrentPageShift
                            ? pageShiftCards(
                                  [visibleCurrentPageShift],
                                  'Current shift',
                              )
                            : null}
                        {pageShiftCards(
                            visibleRemainingPageShifts,
                            visibleCurrentPageShift
                                ? 'Other shifts on this page'
                                : 'Shifts on this page',
                        )}
                        {shouldShowNoPageShiftsMessage ? (
                            <div
                                className={cn(
                                    EXTENSION_SUBTLE_PANEL_CLASS,
                                    'space-y-1',
                                )}
                            >
                                <p className="text-foreground text-sm font-semibold">
                                    No shifts found on this page
                                </p>
                                <p className="text-muted-foreground text-sm">
                                    Phoebe will surface CTA-ready shifts here
                                    when an EHR page exposes them.
                                </p>
                            </div>
                        ) : null}
                    </TabsContent>
                    <TabsContent className="space-y-4" value="conversations">
                        {conversationWorkspace}
                    </TabsContent>
                </Tabs>
            </main>
            {isExtensionAgentChatEnabled ? (
                <Fragment>
                    {!isAgentChatOpen &&
                    !shouldShowSelectableClientShiftCards ? (
                        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 p-3">
                            <button
                                type="button"
                                aria-label={`${agentChatBarLabel}…`}
                                onClick={handleOpenAgentChat}
                                className="border-border/80 bg-background hover:bg-muted/50 pointer-events-auto flex w-full items-center gap-2 rounded-2xl border px-4 py-3 text-left shadow-lg transition-colors"
                            >
                                <Sparkles className="text-muted-foreground h-4 w-4 shrink-0" />
                                <span className="text-muted-foreground truncate text-sm">
                                    {agentChatBarLabel}…
                                </span>
                            </button>
                        </div>
                    ) : null}
                    {hasOpenedAgentChat && resolvedOrganizationId !== null ? (
                        <div
                            className={cn(
                                'bg-background fixed inset-0 z-50',
                                !isAgentChatOpen && 'hidden',
                            )}
                        >
                            <div
                                className="flex h-full min-h-0 w-full flex-col overflow-hidden"
                                onClickCapture={
                                    handleExtensionAppLinkClickCapture
                                }
                            >
                                <ExtensionNavigationGuard />
                                <PhoebeChatView
                                    key={resolvedOrganizationId}
                                    chatMode="sidebar"
                                    clientSurface="extension"
                                    organizationIdOverride={
                                        resolvedOrganizationId
                                    }
                                    isVisible={isAgentChatOpen}
                                    onOpenPhoebePathInNewTab={
                                        openPhoebePathInTab
                                    }
                                    onRequestMicrophonePermission={
                                        requestMicrophonePermission
                                    }
                                    onHide={() => {
                                        setIsAgentChatOpen(false);
                                    }}
                                />
                                <StructuredReportArtifactsPanel
                                    splitMode={false}
                                />
                            </div>
                        </div>
                    ) : null}
                </Fragment>
            ) : null}
        </div>
    );
}
